import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiChevronLeft, FiChevronRight, FiChevronDown, FiPlus, FiRefreshCw, FiZap, FiCheck, FiClock, FiX, FiSearch, FiFileText, FiSend, FiTrendingUp, FiAlertTriangle, FiArrowRight, FiCheckSquare, FiSquare, FiLayers, FiColumns, FiUser, FiMessageSquare, FiHome, FiLock, FiPhone, FiSettings, } from 'react-icons/fi';
import { RiSparklingFill } from 'react-icons/ri';
import { useRateQuoteLink, type QuoteRow, type QuoteRowStatus, type RunEntry, } from './RateQuoteLinkContext';
import { ROUTES } from '../../app/routes';
import InlineWorkflowPanel from './RateQuoteLinkInlineWorkflow';
import QuoteProposalsTab from './QuoteProposalsTab';
import ClassicQuoteTab from './ClassicQuoteTab';

import { getPersonalLineRatingStatus, enablePersonalLineRating, } from '../../services/personalLineRatingService';
import logo from '../../assets/images/M_Multicolor.svg';
import rateLogo from '../../assets/images/Momentum rate Logo 1 (2).svg';
import { RateQuoteLinkImportTab } from './RateQuoteLinkImportTab';
import { useInsuredDetail } from '../../context/InsuredDetailContext';
import { fetchSkill, saveSkill } from '../commerciallinesrating/useCommercialRating';
import '../../styles/RateQuoteLink.css';

const RateMIcon: React.FC<{ size?: number }> = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M9.63 13.16c0-1.55.01-3.1 0-4.65-.01-.83.3-1.52.84-2.11.94-1.02 1.92-2 2.83-3.03.46-.52 1.69-.47 2.01.31.06.14.1.3.1.45.01 3.08.01 6.15 0 9.23-.01.79-.38 1.46-.9 2.03-.98 1.08-1.99 2.13-3.01 3.17-.5.51-1.22.58-1.63.14-.16-.18-.23-.5-.24-.76-.02-1.59-.01-3.18-.01-4.77z" fill="currentColor"/>
    <path d="M21.91 8.78c0 1.5-.01 3-.01 4.5 0 .71-.23 1.33-.71 1.84-1.05 1.12-2.09 2.25-3.17 3.35-.23.24-.58.44-.9.49-.55.09-1.01-.4-1.01-1.02 0-3.17 0-6.34.02-9.5 0-.78.29-1.45.85-2.03.9-.94 1.78-1.9 2.65-2.87.42-.47.9-.68 1.52-.46.5.17.75.51.75 1.04v4.65z" fill="currentColor"/>
    <path d="M8.87 12.29c-.08 2.46.09 2-.4 3.45-.81.88-1.63 1.76-2.46 2.62-.22.23-.49.45-.78.56-.64.26-1.28-.19-1.29-.89-.01-.8-.04-1.59.02-2.38.02-.32.18-.69.4-.93 1.25-1.39 2.54-2.75 3.82-4.13.25-.27.51-.48.9-.46.45.02.71.29.72.75 0 .52 0 1.04 0 1.41z" fill="currentColor"/>
  </svg>
);

// ── PLR Rating Skill Settings ─────────────────────────────────────────────────
const PLRSkillSettings: React.FC = () => {
    const [name, setName] = useState('PLR Rating Profile');
    const [content, setContent] = useState('');
    const [savedAt, setSavedAt] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [saved, setSaved] = useState(false);
    useEffect(() => {
        fetchSkill('PLR').then(skill => {
            if (skill) {
                setName(skill.name);
                setContent(skill.content);
                setSavedAt(skill.updatedAt);
            }
            setLoading(false);
        });
    }, []);
    const handleSave = useCallback(async () => {
        setSaving(true);
        setError(null);
        setSaved(false);
        try {
            const skill = await saveSkill({ lob: 'PLR', name, content });
            setSavedAt(skill.updatedAt);
            setSaved(true);
            setTimeout(() => setSaved(false), 3000);
        } catch (e: any) {
            setError(e.message || 'Save failed');
        } finally {
            setSaving(false);
        }
    }, [name, content]);
    if (loading) {
        return (<div className="rql-skill-settings">
          <div className="rql-table-card">
            <div className="rql-skill-settings__body">
              <div className="rql-skel" style={{ height: 48, borderRadius: 8 }}/>
              <div className="rql-skel" style={{ height: 38, width: 260, borderRadius: 8 }}/>
              <div className="rql-skel" style={{ height: 240, borderRadius: 8 }}/>
            </div>
          </div>
        </div>);
    }
    return (<div className="rql-skill-settings">
      <div className="rql-table-card">
        <div className="rql-table-card__header">
          <div>
            <span className="rql-table-card__title">Rating Skill</span>
            <span className="rql-table-card__subtitle" style={{ marginLeft: 10 }}>
              Personal Lines · PAP · HO
            </span>
          </div>
          {savedAt && (<span className="rql-skill-settings__saved-at">
              Last saved {new Date(savedAt).toLocaleString('en-US', {
                month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
            })}
            </span>)}
        </div>
        <div className="rql-skill-settings__body">
          <p className="rql-skill-settings__hint">
            Write rules the AI will follow when filling quote fields. Describe default values for
            fields not in the template, fallback answers when a template is missing, carrier
            preferences, and any agency-specific quoting rules.
          </p>
          <label className="rql-skill-settings__label">Profile name</label>
          <input className="rql-skill-settings__input" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Standard PLR Rating Profile" maxLength={120}/>
          <label className="rql-skill-settings__label">Rating instructions</label>
          <textarea className="rql-skill-settings__textarea" value={content} onChange={e => setContent(e.target.value)} placeholder={`Example:\nDefault dwelling type: Single Family (1).\nDefault construction type: Frame.\nIf roof year is unknown, use current year minus 8.\nPrior carrier default: State Farm if not in AMS.\nFor HO: set allPerilsDeductible to 1000, windDeductible to 1000.\nFor PAP: default liability to 100/300/100. Set comprehensive and collision deductible to 500.\nDo not ask the user for roof material — default to Asphalt Shingles.\nIf squareFootage is missing, estimate dwellingCoverage at $150 per sq ft minimum $200,000.`} rows={16}/>
          {error && <p className="rql-skill-settings__error">{error}</p>}
          <div className="rql-skill-settings__footer">
            <button className={`rql-skill-settings__save-btn${saved ? ' is-saved' : ''}`} onClick={handleSave} disabled={saving || !content.trim()}>
              {saving ? (<><FiRefreshCw size={13} className="rql-spin"/> Saving…</>) : saved ? (<><FiCheck size={13}/> Saved</>) : ('Save Skill')}
            </button>
          </div>
        </div>
      </div>
    </div>);
};

type ExecuteMode = 'direct' | 'review';
type PolicyType = 'PAP' | 'HO' | 'BUNDLE';
const ExecuteSplitButton: React.FC<{
    onRun: (mode: ExecuteMode, policyType: PolicyType) => void;
}> = ({ onRun }) => {
    const [openMenu, setOpenMenu] = useState<'policy' | 'review' | null>(null);
    const rootRef = useRef<HTMLDivElement | null>(null);
    useEffect(() => {
        if (!openMenu)
            return;
        const onDoc = (e: MouseEvent) => {
            if (!rootRef.current?.contains(e.target as Node))
                setOpenMenu(null);
        };
        document.addEventListener('mousedown', onDoc);
        return () => document.removeEventListener('mousedown', onDoc);
    }, [openMenu]);
    const pick = (policyType: PolicyType, mode: ExecuteMode) => {
        setOpenMenu(null);
        onRun(mode, policyType);
    };
    return (<div className="rql-exec-split" ref={rootRef}>
      
      <button type="button" className="rql-row-execute rql-exec-split__main" onClick={() => setOpenMenu(v => v === 'policy' ? null : 'policy')} title="Select policy type and run">
        <FiZap size={12}/>
        <span>Execute</span>
      </button>

      
      <button type="button" className="rql-exec-split__caret" onClick={() => setOpenMenu(v => v === 'review' ? null : 'review')} aria-haspopup="menu" aria-expanded={openMenu === 'review'} title="Execute with review">
        <FiChevronDown size={12}/>
      </button>

      
      {openMenu === 'policy' && (<div className="rql-exec-menu" role="menu">
          <div className="rql-exec-menu__label-row">Select policy type</div>
          <button type="button" role="menuitem" className="rql-exec-menu__item" onClick={() => pick('PAP', 'direct')}>
            <FiZap size={12}/>
            <span className="rql-exec-menu__text">
              <span className="rql-exec-menu__label">Personal Auto</span>
              <span className="rql-exec-menu__hint">PAP — Drivers &amp; Vehicles</span>
            </span>
          </button>
          <button type="button" role="menuitem" className="rql-exec-menu__item" onClick={() => pick('HO', 'direct')}>
            <FiHome size={12}/>
            <span className="rql-exec-menu__text">
              <span className="rql-exec-menu__label">Home Owners</span>
              <span className="rql-exec-menu__hint">HO — Property &amp; Dwelling</span>
            </span>
          </button>
          <button type="button" role="menuitem" className="rql-exec-menu__item" onClick={() => pick('BUNDLE', 'direct')}>
            <FiLayers size={12}/>
            <span className="rql-exec-menu__text">
              <span className="rql-exec-menu__label">Home + Auto</span>
              <span className="rql-exec-menu__hint">Bundle — Combined HO &amp; PAP</span>
            </span>
          </button>
        </div>)}

      
      {openMenu === 'review' && (<div className="rql-exec-menu" role="menu">
          <div className="rql-exec-menu__label-row">Execute with review</div>
          <button type="button" role="menuitem" className="rql-exec-menu__item" onClick={() => pick('PAP', 'review')}>
            <FiZap size={12}/>
            <span className="rql-exec-menu__text">
              <span className="rql-exec-menu__label">Personal Auto</span>
              <span className="rql-exec-menu__hint">PAP — confirm before carrier submit</span>
            </span>
          </button>
          <button type="button" role="menuitem" className="rql-exec-menu__item" onClick={() => pick('HO', 'review')}>
            <FiHome size={12}/>
            <span className="rql-exec-menu__text">
              <span className="rql-exec-menu__label">Home Owners</span>
              <span className="rql-exec-menu__hint">HO — confirm before carrier submit</span>
            </span>
          </button>
          <button type="button" role="menuitem" className="rql-exec-menu__item" onClick={() => pick('BUNDLE', 'review')}>
            <FiLayers size={12}/>
            <span className="rql-exec-menu__text">
              <span className="rql-exec-menu__label">Home + Auto</span>
              <span className="rql-exec-menu__hint">Bundle — confirm before carrier submit</span>
            </span>
          </button>
        </div>)}
    </div>);
};
type CarrierStatus = 'quoted' | 'declined' | 'failed';
interface CarrierEntry {
    name: string;
    premium: string;
    premiumRaw: number | null;
    termLabel: string;
    monthlyPremium: string;
    status: CarrierStatus;
    type: 'direct' | 'wholesale';
    amBestRating: string;
    logoURL?: string | null;
    bridgingLink?: string | null;
    failureReason?: string | null;
    highlights: string[];
}
interface QuoteRunEntry {
    id: string;
    executedAt: string;
    runStatus: 'completed' | 'failed' | 'running' | 'awaiting_input';
    carriers: CarrierEntry[];
    errorMessage?: string | null;
    durationMs?: number | null;
    policyType?: string | null;
    rawCoverages?: Array<{
        key: string;
        value: string;
    }>;
    submittedDriversCount?: number;
    submittedVehiclesCount?: number;
}
const CARRIER_STATUS_LABEL: Record<CarrierStatus, string> = {
    quoted: 'Quoted',
    declined: 'Declined',
    failed: 'Failed',
};
const CARRIER_META: Record<string, {
    rating: string;
    type: 'direct' | 'wholesale';
}> = {
    'hartford': { rating: 'A+', type: 'direct' },
    'travelers': { rating: 'A++', type: 'direct' },
    'nationwide': { rating: 'A+', type: 'direct' },
    'progressive': { rating: 'A+', type: 'direct' },
    'allstate': { rating: 'A+', type: 'direct' },
    'state farm': { rating: 'A++', type: 'direct' },
    'usaa': { rating: 'A++', type: 'direct' },
    'geico': { rating: 'A++', type: 'direct' },
    'employers': { rating: 'A', type: 'direct' },
    'liberty mutual': { rating: 'A', type: 'wholesale' },
    'cna': { rating: 'A', type: 'wholesale' },
    'markel': { rating: 'A', type: 'wholesale' },
    'amtrust': { rating: 'A-', type: 'wholesale' },
    'berkley': { rating: 'A+', type: 'wholesale' },
};
function getCarrierMeta(name: string): {
    rating: string;
    type: 'direct' | 'wholesale';
} {
    const key = name.toLowerCase();
    for (const [k, v] of Object.entries(CARRIER_META)) {
        if (key.includes(k))
            return v;
    }
    return { rating: 'A', type: 'direct' };
}
const HIGHLIGHTS_BY_RANK: string[][] = [
    ['Lowest overall premium', 'Excellent financial strength', 'Fast claims processing'],
    ['Highest A.M. Best rating', 'Premium customer service', 'Comprehensive coverage'],
    ['Competitive pricing', 'Local agent support', 'Bundle discounts available'],
    ['Flexible payment options', 'Strong claims support', 'Nationwide coverage'],
];
function carrierStatusFromQuote(q: any): CarrierStatus {
    const hasPremium = q.premiumAmount != null && Number(q.premiumAmount) > 0;
    const hasFail = Array.isArray(q.failures) && q.failures.length > 0;
    if (hasPremium)
        return 'quoted';
    if (hasFail)
        return 'failed';
    return 'declined';
}
function formatRunDate(iso: string | null | undefined): string {
    if (!iso)
        return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime()))
        return iso;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
        + ' · ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}
function shortenError(msg: string | null | undefined): string {
    if (!msg)
        return 'An error occurred.';
    const clean = msg.replace(/^RuntimeError:\s*/i, '');
    const cut = clean.split(/[.\n]/)[0].trim();
    return cut.length > 120 ? cut.slice(0, 117) + '…' : cut || clean.slice(0, 120);
}
function mapRunToEntry(r: any): QuoteRunEntry {
    const quotes: any[] = Array.isArray(r.result_payload?.quotes)
        ? r.result_payload.quotes
        : [];
    const quotedSorted = [...quotes]
        .filter(q => q.premiumAmount != null && Number(q.premiumAmount) > 0)
        .sort((a, b) => Number(a.premiumAmount) - Number(b.premiumAmount));
    const carriers: CarrierEntry[] = quotes.map(q => {
        const meta = getCarrierMeta(q.carrierName || q.carrierId || '');
        const premiumRaw = q.premiumAmount != null ? Number(q.premiumAmount) : null;
        const termMonths = Number(q.contractTerm) || 12;
        const monthly = premiumRaw != null ? Math.round(premiumRaw / termMonths) : null;
        const termLabel = termMonths === 12 ? 'yr' : `${termMonths}mo`;
        const status = carrierStatusFromQuote(q);
        const priceRank = quotedSorted.findIndex(x => (x.carrierId && x.carrierId === q.carrierId) || (x.carrierName && x.carrierName === q.carrierName));
        return {
            name: q.carrierName || q.carrierId || 'Unknown',
            premium: premiumRaw != null
                ? `$${premiumRaw.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
                : '—',
            premiumRaw,
            termLabel,
            monthlyPremium: monthly != null ? `$${monthly.toLocaleString()}` : '—',
            status,
            type: meta.type,
            amBestRating: meta.rating,
            logoURL: q.carrierLogoURL ?? null,
            bridgingLink: q.carrierBridgingLink ?? null,
            failureReason: Array.isArray(q.failures) && q.failures.length
                ? q.failures[0]?.reason
                : (q.failureReason ?? null),
            highlights: status === 'quoted'
                ? (HIGHLIGHTS_BY_RANK[priceRank] ?? HIGHLIGHTS_BY_RANK[HIGHLIGHTS_BY_RANK.length - 1])
                : [],
        };
    });
    return {
        id: String(r.id),
        executedAt: formatRunDate(r.started_at),
        runStatus: r.status ?? 'completed',
        carriers,
        errorMessage: r.error_message ?? null,
        durationMs: r.duration_ms ?? null,
        policyType: r.policy_type ?? r.result_payload?.policy_type ?? null,
        rawCoverages: Array.isArray(r.result_payload?.coverages)
            ? r.result_payload.coverages.map((c: any) => ({ key: c.key || '', value: String(c.value || '') }))
            : [],
        submittedDriversCount: r.result_payload?.drivers_count ?? null,
        submittedVehiclesCount: r.result_payload?.vehicles_count ?? null,
    };
}
const amBestTier = (r: string): string => {
    if (!r || r === '—' || r === 'NR' || r === 'N/A' || r === 'n/a') return 'na';
    if (r.startsWith('A++') || r.startsWith('A+')) return 'superior';
    if (r.startsWith('A')) return 'excellent';
    if (r.startsWith('B')) return 'good';
    return 'na';
};
const CarrierResults: React.FC<{
    carriers: CarrierEntry[];
    onProposal?: (carrierName: string) => void;
    onEmailClient?: (carrierName: string) => void;
}> = ({ carriers, onProposal, onEmailClient }) => {
    const [compareMode, setCompareMode] = useState(false);
    const quoted = useMemo(() => carriers.filter(c => c.status === 'quoted'), [carriers]);
    const lowestRaw = useMemo(() => quoted.length ? Math.min(...quoted.map(c => c.premiumRaw ?? Infinity)) : null, [quoted]);
    const sorted = useMemo(() => {
        const list = [...carriers];
        list.sort((a, b) => {
            if (a.status !== b.status)
                return a.status === 'quoted' ? -1 : 1;
            return (a.premiumRaw ?? Infinity) - (b.premiumRaw ?? Infinity);
        });
        return list;
    }, [carriers]);
    if (sorted.length === 0)
        return null;
    const allDeclined = quoted.length === 0;

    /* ── Clean "no quotes" state — early return ── */
    if (allDeclined) {
        return (<div className="rql-no-quotes">
          <div className="rql-no-quotes__top">
            <div className="rql-no-quotes__icon-wrap"><FiAlertTriangle size={13}/></div>
            <div className="rql-no-quotes__text">
              <span className="rql-no-quotes__title">No quotes received</span>
              <span className="rql-no-quotes__sub">{sorted.length} carrier{sorted.length !== 1 ? 's' : ''} declined or failed for this submission</span>
            </div>
          </div>
          <div className="rql-no-quotes__list">
            {sorted.map(c => (<div key={c.name} className={`rql-no-quotes__item rql-no-quotes__item--${c.status}`}>
                <span className="rql-no-quotes__name">{c.name}</span>
                <span className={`rql-no-quotes__badge rql-no-quotes__badge--${c.status}`}>{c.status === 'declined' ? 'Declined' : 'Failed'}</span>
                {c.failureReason && <span className="rql-no-quotes__reason">{shortenError(c.failureReason)}</span>}
              </div>))}
          </div>
          <p className="rql-no-quotes__hint">Adjust coverages, effective date, or carrier appetite and re-run the workflow.</p>
        </div>);
    }

    const COMPARE_ROWS: Array<{
        label: string;
        render: (c: CarrierEntry) => React.ReactNode;
    }> = [
        { label: 'Status', render: c => <span className={`rql-cmp__status rql-cmp__status--${c.status}`}>{CARRIER_STATUS_LABEL[c.status]}</span> },
        { label: 'Annual', render: c => <strong>{c.premium}</strong> },
        { label: 'Monthly', render: c => c.monthlyPremium },
        { label: 'AM Best', render: c => <span className="rql-cmp__rating">{c.amBestRating}</span> },
        { label: 'Type', render: c => <span className={`rql-cmp__type rql-cmp__type--${c.type}`}>{c.type === 'direct' ? 'Direct' : 'Wholesale'}</span> },
        { label: 'Bind', render: c => c.bridgingLink ? <a href={c.bridgingLink} target="_blank" rel="noopener noreferrer" className="rql-cmp__bind">Bind</a> : c.status === 'quoted' ? <span className="rql-cmp__bind rql-cmp__bind--soft">Bind</span> : <span className="rql-cmp__na">—</span> },
    ];
    return (<div className="rql-cards">
      {quoted.length >= 2 && (<div className="rql-cards__toolbar">
          <button type="button" className={`rql-view-toggle${compareMode ? ' rql-view-toggle--active' : ''}`} onClick={() => setCompareMode(v => !v)} title={compareMode ? 'Switch to card view' : 'Compare carriers side-by-side'}>
            <FiColumns size={11}/>
            {compareMode ? 'Card view' : 'Compare'}
          </button>
        </div>)}
      {compareMode ? (<div className="rql-cmp">
          <div className="rql-cmp__scroll">
            <table className="rql-cmp__table">
              <thead>
                <tr>
                  <th className="rql-cmp__row-label"/>
                  {sorted.filter(c => c.status === 'quoted').map(c => (<th key={c.name} className={`rql-cmp__col-head${c.premiumRaw === lowestRaw ? ' rql-cmp__col-head--best' : ''}`}>
                      <div className="rql-cmp__carrier-name">{c.name.charAt(0).toUpperCase()}</div>
                      <div className="rql-cmp__carrier-label">{c.name}</div>
                      {c.premiumRaw === lowestRaw && <span className="rql-cmp__best-badge">Best</span>}
                    </th>))}
                </tr>
              </thead>
              <tbody>
                {COMPARE_ROWS.map(row => (<tr key={row.label}>
                    <td className="rql-cmp__row-label">{row.label}</td>
                    {sorted.filter(c => c.status === 'quoted').map(c => (<td key={c.name} className={`rql-cmp__cell${c.premiumRaw === lowestRaw ? ' rql-cmp__cell--best' : ''}`}>
                        {row.render(c)}
                      </td>))}
                  </tr>))}
              </tbody>
            </table>
          </div>
          {sorted.filter(c => c.status !== 'quoted').length > 0 && (<div className="rql-cmp__not-quoted">
              {sorted.filter(c => c.status !== 'quoted').map(c => (<span key={c.name} className={`rql-cmp__nq-chip rql-cmp__nq-chip--${c.status}`}>
                  {c.name} — {c.status}
                  {c.failureReason && <span className="rql-cmp__nq-reason"> ({shortenError(c.failureReason)})</span>}
                </span>))}
            </div>)}
        </div>) : (sorted.map(c => {
            const isBest = c.status === 'quoted' && c.premiumRaw === lowestRaw;
            return (<div key={c.name} className={`rql-card rql-card--${c.status}${isBest ? ' rql-card--best' : ''}`}>
              <div className="rql-card__identity">
                <div className="rql-card__carrier">
                  {c.logoURL
                    ? <div className="rql-card__logo-wrap"><img src={c.logoURL} className="rql-card__logo" alt={c.name}/></div>
                    : <span className="rql-card__initial">{c.name.charAt(0).toUpperCase()}</span>}
                  <div className="rql-card__carrier-meta">
                    <span className="rql-card__carrier-name">{c.name}</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                      <span className={`rql-card__type-badge rql-card__type-badge--${c.type}`}>
                        {c.type === 'direct' ? <FiCheck size={8}/> : <FiRefreshCw size={8}/>}
                        {c.type === 'direct' ? 'Direct' : 'Wholesale'}
                      </span>
                      {isBest && <span className="rql-card__rec">★ Best Pick</span>}
                    </div>
                  </div>
                </div>
              </div>
              <div className="rql-card__pricing">
                <div className="rql-card__ambest">
                  <span className="rql-card__ambest-label">AM Best</span>
                  <span className="rql-card__rating" data-tier={amBestTier(c.amBestRating)}>{c.amBestRating || '—'}</span>
                </div>
                <div className="rql-card__pricing-sep"/>
                {c.status === 'quoted' ? (<div className="rql-card__price-stack">
                    <span className="rql-card__annual">{c.premium}<span className="rql-card__annual-unit">/{c.termLabel}</span></span>
                    <span className="rql-card__monthly">{c.monthlyPremium}/mo</span>
                  </div>) : (<span className={`rql-card__no-quote rql-card__no-quote--${c.status}`}>
                    {c.status === 'declined' ? 'Declined' : 'Failed'}
                  </span>)}
              </div>
              <div className="rql-card__actions" onClick={e => e.stopPropagation()}>
                {c.status === 'quoted' ? (<>
                    {c.bridgingLink ? (<a href={c.bridgingLink} target="_blank" rel="noopener noreferrer" className="rql-card__btn rql-card__btn--bind">
                        <FiTrendingUp size={11}/> Bind
                      </a>) : (<button type="button" className="rql-card__btn rql-card__btn--bind">
                        <FiTrendingUp size={11}/> Bind
                      </button>)}
                    <button type="button" className="rql-card__btn" onClick={() => onProposal?.(c.name)}><FiFileText size={11}/> Proposal</button>
                    <button type="button" className="rql-card__btn" onClick={() => onEmailClient?.(c.name)}><FiSend size={11}/> Email Client</button>
                  </>) : (<span className={`rql-card__status-chip rql-card__status-chip--${c.status}`}>
                    <span className="rql-card__status-dot"/>
                    {CARRIER_STATUS_LABEL[c.status]}
                  </span>)}
              </div>
            </div>);
        }))}
    </div>);
};
const RATE_QUOTING_BASE = `${process.env.REACT_APP_MyAMP_Url}/api/ams/rate-quoting`;
const RowExpand: React.FC<{
    colSpan: number;
    runs: QuoteRunEntry[];
    isLoading: boolean;
    onOpenWorkflow: (runId: string) => void;
    onOpenWorkflowForEmail?: (runId: string, carrierName: string) => void;
    onRefresh: () => void;
    onSelectRun: (run: QuoteRunEntry, runNumber: number) => void;
    onExecute?: () => void;
}> = ({ colSpan, runs, isLoading, onOpenWorkflow, onOpenWorkflowForEmail, onRefresh, onSelectRun, onExecute }) => {
    const { reattachRun } = useRateQuoteLink();
    const [expandedRunId, setExpandedRunId] = useState<string | null>(null);
    return (<tr className="rql-expand-row">
    <td colSpan={colSpan} className="rql-expand-row__cell">
      <div className="rql-expand">

        
        <div className="rql-hist-head">
          <span className="rql-hist-head__icon"><FiZap size={11}/></span>
          <span className="rql-hist-head__title">Quote History</span>
          {!isLoading && (<span className="rql-hist-head__count">{runs.length}</span>)}
          <button type="button" className="rql-hist-head__refresh" onClick={e => { e.stopPropagation(); onRefresh(); }} disabled={isLoading} title="Refresh quote history">
            <FiRefreshCw size={11} className={isLoading ? 'rql-row-execute__spin' : ''}/>
          </button>
        </div>

        
        {isLoading ? (<div className="rql-hist-spinner">
            <div className="rql-hist-spinner__ring"/>
          </div>) : runs.length === 0 ? (<div className="rql-expand__empty">
            <div className="rql-expand__empty-msg">
              <FiZap size={13} style={{ marginRight: 6, opacity: 0.4 }}/>
              No quote runs yet — start the AI workflow to get carrier quotes.
            </div>
            {onExecute && (<button type="button" className="rql-expand__cta" onClick={e => { e.stopPropagation(); onExecute(); }}>
                <FiZap size={11}/> Start Quote Workflow
                <FiArrowRight size={11}/>
              </button>)}
          </div>) : (<div className="rql-run-list">
            {runs.map((run, ri) => {
                const isActive = run.runStatus === 'running' || run.runStatus === 'awaiting_input';
                const hasViewDetailsBtn = run.runStatus === 'completed' || run.runStatus === 'failed';
                const isHdrClickable = false;
                const lobRaw = (run.policyType || '').toUpperCase();
                const lobCls = lobRaw === 'HO' ? 'ho' : lobRaw === 'BUNDLE' ? 'bundle' : lobRaw === 'PAP' ? 'pap' : '';
                const lobLabel = lobRaw === 'HO' ? 'Home' : lobRaw === 'BUNDLE' ? 'Auto + Home' : lobRaw === 'PAP' ? 'Auto' : '';
                return (<div key={run.id} className={`rql-run-block rql-run-block--${run.runStatus}`}>

                  
                  <div className={`rql-run-block__hdr${isHdrClickable ? ' rql-run-block__hdr--clickable' : ''}`} onClick={() => { if (isHdrClickable) onSelectRun(run, ri + 1); }}>

                    {/* ── Row 1: identity left · actions right ── */}
                    <div className="rql-run-block__hdr-row">
                      <div className="rql-run-block__ident">
                        <span className={`rql-run-block__dot rql-run-block__dot--${run.runStatus}`}/>
                        <span className="rql-run-block__num">Quote #{ri + 1}</span>
                        {lobCls && (<span className={`rql-run-block__lob rql-run-block__lob--${lobCls}`}>
                            {lobRaw === 'PAP' ? <FiUser size={8}/> : lobRaw === 'HO' ? <FiHome size={8}/> : <FiLayers size={8}/>}
                            {lobLabel}
                          </span>)}
                        {run.runStatus === 'completed' && (<span className="rql-run-block__badge rql-run-block__badge--ok"><FiCheck size={9}/> Completed</span>)}
                        {run.runStatus === 'failed'    && (<span className="rql-run-block__badge rql-run-block__badge--err"><FiX size={9}/> Failed</span>)}
                        {isActive && (<span className="rql-run-block__badge rql-run-block__badge--running"><FiRefreshCw size={9} className="rql-row-execute__spin"/> {run.runStatus === 'awaiting_input' ? 'Awaiting input' : 'Running'}</span>)}
                      </div>
                      <div className="rql-run-block__acts">
                        {isActive && (<button type="button" className={`rql-run-item__cta${run.runStatus === 'awaiting_input' ? ' rql-run-item__cta--review' : ''}`} onClick={e => { e.stopPropagation(); reattachRun(run.id); onOpenWorkflow(run.id); }}><FiArrowRight size={10}/> Review &amp; Submit</button>)}
                        {run.runStatus === 'completed' && (<button type="button" className="rql-run-item__cta rql-run-item__cta--rate" onClick={async e => { e.stopPropagation(); try { const myampUrl = process.env.REACT_APP_MyAMP_Url || ''; const tok = localStorage.getItem('authToken') || ''; const res = await fetch(`${myampUrl}/api/ams/rate-quoting/runs/${run.id}/rate-url`, { headers: { Authorization: `Bearer ${tok}` } }); if (res.ok) { const data = await res.json(); if (data.rate_url) window.open(data.rate_url, '_blank'); } } catch { /* silently fail */ } }}><FiArrowRight size={10}/> Go to Rate</button>)}
                        {hasViewDetailsBtn && (<button type="button" className="rql-run-item__cta rql-run-item__cta--details" onClick={e => { e.stopPropagation(); reattachRun(run.id); onOpenWorkflow(run.id); }}><FiMessageSquare size={10}/> View Details</button>)}
                      </div>
                    </div>

                    {/* ── Row 2: meta — date · duration · quote tally ── */}
                    <div className="rql-run-block__meta-row">
                      <span className="rql-run-block__date">{run.executedAt}</span>
                      {run.durationMs != null && (<span className="rql-run-block__dur"><FiClock size={9}/> {(run.durationMs / 1000).toFixed(1)}s</span>)}
                      {run.carriers.length > 0 && (<span className="rql-run-block__quote-count">{run.carriers.filter(c => c.status === 'quoted').length}<span className="rql-run-block__qc-sep">/</span>{run.carriers.length} quoted</span>)}
                    </div>

                  </div>

                  
                  {run.runStatus === 'completed' && (run.rawCoverages?.length || run.submittedDriversCount) && (<button type="button" className={`rql-run-drilldown-btn${expandedRunId === run.id ? ' rql-run-drilldown-btn--open' : ''}`} onClick={e => { e.stopPropagation(); setExpandedRunId(p => p === run.id ? null : run.id); }}>
                      <FiChevronDown size={10} className="rql-run-drilldown-btn__icon"/>
                      {expandedRunId === run.id ? 'Hide details' : 'View submitted details'}
                    </button>)}
                  {expandedRunId === run.id && (<div className="rql-run-drilldown">
                      {(run.submittedDriversCount || run.submittedVehiclesCount) && (<div className="rql-run-drilldown__meta">
                          {run.submittedDriversCount != null && <span className="rql-run-drilldown__chip"><FiUser size={9}/> {run.submittedDriversCount} driver{run.submittedDriversCount !== 1 ? 's' : ''}</span>}
                          {run.submittedVehiclesCount != null && <span className="rql-run-drilldown__chip"><FiFileText size={9}/> {run.submittedVehiclesCount} vehicle{run.submittedVehiclesCount !== 1 ? 's' : ''}</span>}
                        </div>)}
                      {run.rawCoverages && run.rawCoverages.length > 0 && (<div className="rql-run-drilldown__coverages">
                          <div className="rql-run-drilldown__cov-head">Submitted coverages</div>
                          <dl className="rql-run-drilldown__cov-grid">
                            {run.rawCoverages.slice(0, 12).map((c, ci) => (<React.Fragment key={ci}>
                                <dt>{c.key.replace(/_/g, ' ')}</dt>
                                <dd>{c.value}</dd>
                              </React.Fragment>))}
                          </dl>
                        </div>)}
                    </div>)}

                  
                  {run.runStatus === 'failed' && run.carriers.length === 0 && run.errorMessage && (<div className="rql-run-block__err">
                      <FiAlertTriangle size={11}/>
                      <span>{shortenError(run.errorMessage)}</span>
                    </div>)}

                  
                  {isActive && run.carriers.length === 0 && (<div className="rql-run-block__progress">
                      {run.runStatus === 'awaiting_input' ? (<>
                          <FiArrowRight size={11} className="rql-run-item__progress-icon rql-run-item__progress-icon--review"/>
                          <span className="rql-run-item__progress-label rql-run-item__progress-label--review">
                            Paused · Your review is needed to proceed
                          </span>
                        </>) : (<>
                          <span className="rql-run-item__progress-dot"/>
                          <span className="rql-run-item__progress-dot" style={{ animationDelay: '0.2s' }}/>
                          <span className="rql-run-item__progress-dot" style={{ animationDelay: '0.4s' }}/>
                          <span className="rql-run-item__progress-label">AI agent working…</span>
                        </>)}
                    </div>)}

                  
                  {run.carriers.length > 0 && (<CarrierResults carriers={run.carriers} onProposal={(name) => { reattachRun(run.id); onOpenWorkflow(run.id); }} onEmailClient={(name) => { reattachRun(run.id); onOpenWorkflowForEmail?.(run.id, name); }}/>)}
                </div>);
            })}
          </div>)}
      </div>
    </td>
  </tr>);
};
type TabKey = 'classic' | 'new' | 'renewal' | 'import' | 'winback' | 'history' | 'proposals' | 'settings';
interface HistoryRun {
    id: string;
    insured_name: string;
    workflow: string;
    trigger: string;
    executed_by: string;
    status: string;
    started_at: string | null;
    completed_at: string | null;
    duration_ms: number | null;
    policy_type: string | null;
    state: string | null;
    effective_date: string | null;
    carriers_total: number;
    carriers_quoted: number;
    best_premium: number | null;
    best_carrier: string | null;
    best_contract_term: string | null;
    error_message: string | null;
    application_id: string | null;
}
interface HistoryStats {
    total: number;
    quoted: number;
    failed: number;
    pap: number;
    ho: number;
    bundle: number;
}
const HIST_BASE = `${process.env.REACT_APP_MyAMP_Url}/api/ams/rate-quoting`;
const HistoryTab: React.FC<{
    runs: RunEntry[] | null;
    onOpenRun?: (runId: string) => void;
}> = ({ onOpenRun }) => {
    const [data, setData] = useState<HistoryRun[]>([]);
    const [stats, setStats] = useState<HistoryStats>({ total: 0, quoted: 0, failed: 0, pap: 0, ho: 0, bundle: 0 });
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [filterType, setFilterType] = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const perPage = 15;
    const fetchHistory = useCallback(async (p: number, s: string, ft: string, fs: string, df: string, dt: string) => {
        setLoading(true);
        const token = localStorage.getItem('authToken') || '';
        const params = new URLSearchParams({ page: String(p), limit: String(perPage) });
        if (s)
            params.set('search', s);
        if (ft)
            params.set('policy_type', ft);
        if (fs)
            params.set('status', fs);
        if (df)
            params.set('date_from', df);
        if (dt)
            params.set('date_to', dt);
        try {
            const resp = await fetch(`${HIST_BASE}/history?${params}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (resp.ok) {
                const json = await resp.json();
                setData(json.runs ?? []);
                setTotal(json.total ?? 0);
                setTotalPages(json.total_pages ?? 1);
                if (json.stats)
                    setStats(json.stats);
            }
        }
        catch { }
        setLoading(false);
    }, []);
    useEffect(() => { fetchHistory(page, search, filterType, filterStatus, dateFrom, dateTo); }, [page, filterType, filterStatus, dateFrom, dateTo]);
    const onSearchChange = (v: string) => {
        setSearch(v);
        if (searchTimer.current)
            clearTimeout(searchTimer.current);
        searchTimer.current = setTimeout(() => { setPage(1); fetchHistory(1, v, filterType, filterStatus, dateFrom, dateTo); }, 400);
    };
    const fmtDate = (d?: string | null) => {
        if (!d)
            return '—';
        try {
            return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        }
        catch {
            return d;
        }
    };
    const fmtTime = (d?: string | null) => {
        if (!d)
            return '';
        try {
            return new Date(d).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
        }
        catch {
            return '';
        }
    };
    const fmtDur = (ms?: number | null) => {
        if (!ms)
            return '';
        const s = Math.round(ms / 1000);
        return s >= 60 ? `${Math.floor(s / 60)}m ${s % 60}s` : `${s}s`;
    };
    return (<div className="rql-hist">
      
      <div className="rql-hist__stats">
        {[
            { label: 'Total Runs', val: stats.total, icon: <FiLayers size={13}/>, accent: '#a200ff' },
            { label: 'Quoted', val: stats.quoted, icon: <FiCheck size={13}/>, accent: '#22c55e' },
            { label: 'Failed', val: stats.failed, icon: <FiAlertTriangle size={13}/>, accent: '#ef4444' },
            { label: 'Auto', val: stats.pap, icon: <FiUser size={13}/>, accent: '#6366f1' },
            { label: 'Home', val: stats.ho, icon: <FiHome size={13}/>, accent: '#10b981' },
            { label: 'Bundle', val: stats.bundle, icon: <FiLayers size={13}/>, accent: '#8854bc' },
        ].map((s, i) => (<div key={i} className="rql-hist__stat" style={{ '--stat-accent': s.accent } as React.CSSProperties}>
            <div className="rql-hist__stat-icon">{s.icon}</div>
            <div className="rql-hist__stat-body">
              <span className="rql-hist__stat-val">{s.val}</span>
              <span className="rql-hist__stat-lbl">{s.label}</span>
            </div>
          </div>))}
      </div>

      
      <div className="rql-hist__filters">
        <div className="rql-hist__search">
          <FiSearch size={13}/>
          <input type="text" placeholder="Search insured name..." value={search} onChange={e => onSearchChange(e.target.value)}/>
        </div>
        <div className="rql-hist__pills">
          {(['', 'PAP', 'HO', 'BUNDLE'] as const).map(t => (<button key={t || 'all'} className={`rql-hist__pill${filterType === t ? ' rql-hist__pill--active' : ''}`} onClick={() => { setFilterType(t); setPage(1); }}>
              {!t ? 'All' : t === 'PAP' ? 'Auto' : t === 'HO' ? 'Home' : 'Bundle'}
            </button>))}
          <span className="rql-hist__pill-sep"/>
          {(['', 'completed', 'failed'] as const).map(s => (<button key={s || 'all'} className={`rql-hist__pill${filterStatus === s ? ' rql-hist__pill--active' : ''}`} onClick={() => { setFilterStatus(s); setPage(1); }}>
              {!s ? 'All' : s === 'completed' ? 'Quoted' : 'Failed'}
            </button>))}
        </div>
        <div className="rql-hist__date-range">
          <FiClock size={11}/>
          <input type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setPage(1); }}/>
          <span className="rql-hist__date-sep">–</span>
          <input type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); setPage(1); }}/>
          {(dateFrom || dateTo) && (<button className="rql-hist__date-clear" onClick={() => { setDateFrom(''); setDateTo(''); setPage(1); }}>
              <FiX size={11}/>
            </button>)}
        </div>
        <span className="rql-hist__count">
          {total} submission{total !== 1 ? 's' : ''}
        </span>
      </div>

      
      {loading ? (<div className="rql-hist__list" style={{ flex: 1 }}>
          {Array.from({ length: 12 }).map((_, i) => (<div key={i} className="rql-hist__card rql-hist__skel-card">
              <div className="rql-hist__card-left">
                <div className="rql-hist__skel rql-hist__skel--avatar"/>
                <div className="rql-hist__card-ident">
                  <div className="rql-hist__skel rql-hist__skel--name"/>
                  <div className="rql-hist__skel rql-hist__skel--meta"/>
                </div>
              </div>
              <div className="rql-hist__card-right">
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 5 }}>
                  <div className="rql-hist__skel rql-hist__skel--price"/>
                  <div className="rql-hist__skel rql-hist__skel--carrier"/>
                </div>
                <div className="rql-hist__skel rql-hist__skel--badge"/>
              </div>
            </div>))}
        </div>) : (<div className="rql-hist__list">
          {data.length === 0 ? (<div className="rql-hist__empty">
              <div className="rql-hist__empty-icon">
                {search || filterType || filterStatus || dateFrom || dateTo
                    ? <FiSearch size={32}/>
                    : <FiClock size={32}/>}
              </div>
              <span className="rql-hist__empty-title">
                {search || filterType || filterStatus || dateFrom || dateTo
                    ? 'No submissions found'
                    : 'No submission history yet'}
              </span>
              <span className="rql-hist__empty-hint">
                {search || filterType || filterStatus || dateFrom || dateTo
                    ? 'Try adjusting your search, filters, or date range to find what you\'re looking for.'
                    : 'Run a quote from the Prospects or Renewal tab to see your submission history here.'}
              </span>
              {(search || filterType || filterStatus || dateFrom || dateTo) && (<button className="rql-hist__empty-clear" onClick={() => {
                        setSearch('');
                        setFilterType('');
                        setFilterStatus('');
                        setDateFrom('');
                        setDateTo('');
                        setPage(1);
                    }}>
                  <FiX size={12}/> Clear all filters
                </button>)}
            </div>) : data.map(r => {
                const initials = (r.insured_name || '?').split(/[\s,]+/).filter(Boolean).map(w => w[0]?.toUpperCase()).join('').slice(0, 2);
                const pt = r.policy_type || '—';
                const bestTerm = Number(r.best_contract_term) || 12;
                const bestLabel = bestTerm === 12 ? 'yr' : `${bestTerm}mo`;
                return (<div key={r.id} className={`rql-hist__card rql-hist__card--${r.status}`} onClick={() => onOpenRun?.(r.id)} style={{ cursor: 'pointer' }}>
                <div className="rql-hist__card-left">
                  <div className={`rql-hist__avatar rql-hist__avatar--${pt.toLowerCase()}`}>{initials}</div>
                  <div className="rql-hist__card-ident">
                    <div className="rql-hist__card-name">
                      {r.insured_name || '—'}
                      {r.trigger === 'ai' && <span className="rql-hist__ai-badge"><RiSparklingFill size={8}/></span>}
                      {r.workflow === 'renewal' && <span className="rql-hist__renew-badge">Renewal</span>}
                      {r.state && <span className="rql-hist__state-badge">{r.state}</span>}
                    </div>
                    <div className="rql-hist__card-meta">
                      <span className={`rql-hist__type rql-hist__type--${pt.toLowerCase()}`}>
                        {pt === 'PAP' ? 'Personal Auto' : pt === 'HO' ? 'Home' : pt === 'BUNDLE' ? 'Home + Auto' : pt}
                      </span>
                      <span className="rql-hist__meta-dot"/>
                      <span>{r.executed_by}</span>
                      <span className="rql-hist__meta-dot"/>
                      <span>{fmtDate(r.started_at)} {fmtTime(r.started_at)}</span>
                      {r.duration_ms && <>
                        <span className="rql-hist__meta-dot"/>
                        <FiClock size={9}/> {fmtDur(r.duration_ms)}
                      </>}
                      {r.effective_date && <>
                        <span className="rql-hist__meta-dot"/>
                        Eff: {r.effective_date}
                      </>}
                    </div>
                  </div>
                </div>
                <div className="rql-hist__card-right">
                  {r.best_premium ? (<div className="rql-hist__card-premium">
                      <span className="rql-hist__card-amount">
                        ${r.best_premium.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                        <span className="rql-hist__card-term">/{bestLabel}</span>
                      </span>
                      <span className="rql-hist__card-carrier">
                        {r.best_carrier}
                        {r.carriers_total > 1 && <span className="rql-hist__card-qcount"> · {r.carriers_quoted}/{r.carriers_total} quoted</span>}
                      </span>
                    </div>) : r.carriers_total > 0 ? (<div className="rql-hist__card-premium rql-hist__card-premium--none">
                      <span className="rql-hist__card-no-quote">No premiums</span>
                      <span className="rql-hist__card-carrier">{r.carriers_total} carrier{r.carriers_total !== 1 ? 's' : ''}</span>
                    </div>) : null}
                  <div className="rql-hist__card-actions">
                    <span className={`rql-hist__status rql-hist__status--${r.status}`}>
                      {r.status === 'completed' ? 'Quoted' : r.status === 'failed' ? 'Failed'
                          : r.status === 'running' ? 'Running' : 'Awaiting'}
                    </span>
                    {r.application_id && (r.status === 'completed' || r.status === 'failed') && (
                      <button type="button" className="rql-iwf__go-to-rate" onClick={async e => {
                        e.stopPropagation();
                        try {
                          const tok = localStorage.getItem('authToken') || '';
                          const res = await fetch(`${HIST_BASE}/runs/${r.id}/rate-url`, { headers: { Authorization: `Bearer ${tok}` } });
                          if (res.ok) { const d = await res.json(); if (d.rate_url) window.open(d.rate_url, '_blank'); }
                        } catch { /* silently fail */ }
                      }}><RateMIcon size={12} /> Go to Rate</button>
                    )}
                  </div>
                </div>
              </div>);
            })}
        </div>)}

      
      {totalPages > 1 && (<div className="rql-hist__pagination">
          <button disabled={page <= 1} onClick={() => setPage(1)}><FiChevronLeft size={14}/><FiChevronLeft size={14} style={{ marginLeft: -8 }}/></button>
          <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}><FiChevronLeft size={14}/></button>
          <span className="rql-hist__page-info">Page {page} of {totalPages}</span>
          <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}><FiChevronRight size={14}/></button>
          <button disabled={page >= totalPages} onClick={() => setPage(totalPages)}><FiChevronRight size={14}/><FiChevronRight size={14} style={{ marginLeft: -8 }}/></button>
        </div>)}
    </div>);
};
const STATUS_LABEL: Record<QuoteRowStatus, string> = {
    pending: 'Pending',
    'in-progress': 'In Progress',
    completed: 'Completed',
    failed: 'Failed',
};
const SkeletonRow: React.FC<{
    tab: TabKey;
}> = ({ tab }) => (<tr className="rql-table__row rql-table__row--skel">
    
    <td className="rql-table__col-check" style={{ display: 'none' }}><span className="rql-skel rql-skel--icon"/></td>
    
    <td className="rql-table__col-expand">
      <span className="rql-skel rql-skel--icon"/>
    </td>
    
    <td className="rql-table__cell--primary">
      <span className="rql-skel rql-skel--xl"/>
    </td>
    {tab === 'new' && (<td><span className="rql-skel rql-skel--md"/></td>)}
    {tab === 'renewal' && (<>
        <td><span className="rql-skel rql-skel--sm"/></td>
        <td><span className="rql-skel rql-skel--sm"/></td>
        <td><span className="rql-skel rql-skel--sm"/></td>
        <td><span className="rql-skel rql-skel--xs"/></td>
      </>)}
    
    <td><span className="rql-skel rql-skel--pill"/></td>
    
    <td className="rql-table__col-action">
      <span className="rql-skel rql-skel--btn"/>
    </td>
  </tr>);
const SKEL_ROWS = 8;
type DateRange = {
    start: string;
    end: string;
};
const EMPTY_RANGE: DateRange = { start: '', end: '' };
const ROWS_PER_PAGE = 25;
function parseRowDate(raw?: string): Date | null {
    if (!raw)
        return null;
    const d = new Date(raw);
    return Number.isNaN(d.getTime()) ? null : d;
}
function inRange(value: Date, range: DateRange): boolean {
    if (range.start) {
        const lo = new Date(range.start);
        if (value < lo)
            return false;
    }
    if (range.end) {
        const hi = new Date(range.end);
        hi.setHours(23, 59, 59, 999);
        if (value > hi)
            return false;
    }
    return true;
}
const RateQuoteLinkPage: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const urlParts = location.pathname.replace(/^\/rate-quoting\/?/, '').split('/').filter(Boolean);
    const urlRunId = urlParts[0] === 'run' && urlParts[1] ? urlParts[1] : null;
    const urlTabSegment = urlRunId ? '' : (urlParts[0] || '');
    const TAB_SEGMENTS: Record<string, TabKey> = { '': 'classic', classic: 'classic', new: 'new', renewal: 'renewal', import: 'import', history: 'history', winback: 'winback', proposals: 'proposals', summaries: 'proposals', settings: 'settings' };
    const hasAutoQuoteParam = Boolean(new URLSearchParams(window.location.search).get('autoQuote_insured'));
    const initialTab = urlRunId ? 'new' : hasAutoQuoteParam ? 'new' : (TAB_SEGMENTS[urlTabSegment] ?? 'classic');
    const [activeTab, setActiveTabRaw] = useState<TabKey>(initialTab);
    const [historyCount, setHistoryCount] = useState(0);
    const [expandedRowId, setExpandedRowId] = useState<string | null>(null);
    const [activeInlineRunId, setActiveInlineRunId] = useState<string | null>(null);
    const [inlineInitialMessage, setInlineInitialMessage] = useState<string | undefined>(undefined);
    const [selectedRowIds, setSelectedRowIds] = useState<Set<string>>(new Set());
    const [bulkRunning, setBulkRunning] = useState(false);
    const [plrConfigured, setPlrConfigured] = useState<boolean | null>(null);
    const [plrSetupBusy, setPlrSetupBusy] = useState(false);
    const [plrSetupError, setPlrSetupError] = useState<string | null>(null);
    useEffect(() => {
        getPersonalLineRatingStatus()
            .then(s => setPlrConfigured(s.isEnabled))
            .catch(() => setPlrConfigured(true));
    }, []);
    const handleSetupPlr = useCallback(async () => {
        setPlrSetupBusy(true);
        setPlrSetupError(null);
        try {
            let rateJwt = localStorage.getItem('rateToken') || '';
            if (!rateJwt) {
                const amsToken = localStorage.getItem('amsToken') || localStorage.getItem('nc_token') || '';
                if (!amsToken) {
                    setPlrSetupError('Session token not found. Please sign out and back in, then try again.');
                    return;
                }
                const tokenRes = await fetch(`${(process.env.REACT_APP_Auth_Url || '')}/api/account/ratetoken?nctoken=${encodeURIComponent(amsToken)}`);
                if (tokenRes.ok) {
                    const data = await tokenRes.json();
                    rateJwt = data?.igToken || '';
                    if (rateJwt)
                        localStorage.setItem('rateToken', rateJwt);
                }
            }
            if (!rateJwt) {
                setPlrSetupError('Could not obtain Rate API token. Please try again.');
                return;
            }
            try {
                const payload = JSON.parse(atob(rateJwt.split('.')[1]));
                if (!payload.StripeCustomerId || !payload.StripeSubscriptionId || !payload.SubscriptionType) {
                    setPlrSetupError('NOT_SUBSCRIBED');
                    return;
                }
            }
            catch {
            }
            const result = await enablePersonalLineRating(rateJwt);
            if (result.success) {
                setPlrConfigured(true);
            }
            else if (result.code === 'NOT_SUBSCRIBED') {
                setPlrSetupError('NOT_SUBSCRIBED');
            }
            else {
                setPlrSetupError(result.message || 'Setup failed. Please try again.');
            }
        }
        catch (err: any) {
            setPlrSetupError('Setup failed. Please try again.');
        }
        finally {
            setPlrSetupBusy(false);
        }
    }, []);
    const [rowRunsCache, setRowRunsCache] = useState<Record<string, QuoteRunEntry[]>>({});
    const [rowRunsLoading, setRowRunsLoading] = useState<Record<string, boolean>>({});
    const fetchRowRuns = useCallback(async (rowId: string, force = false) => {
        if (!force && (rowRunsCache[rowId] !== undefined || rowRunsLoading[rowId]))
            return;
        setRowRunsLoading(prev => ({ ...prev, [rowId]: true }));
        try {
            const authToken = localStorage.getItem('authToken') || '';
            const res = await fetch(`${RATE_QUOTING_BASE}/runs/by-row/${encodeURIComponent(rowId)}?limit=20`, { headers: { Authorization: `Bearer ${authToken}` } });
            if (res.ok) {
                const data = await res.json();
                const runs: QuoteRunEntry[] = Array.isArray(data.runs)
                    ? data.runs.map(mapRunToEntry)
                    : [];
                setRowRunsCache(prev => ({ ...prev, [rowId]: runs }));
            }
            else {
                setRowRunsCache(prev => ({ ...prev, [rowId]: [] }));
            }
        }
        catch {
            setRowRunsCache(prev => ({ ...prev, [rowId]: [] }));
        }
        finally {
            setRowRunsLoading(prev => ({ ...prev, [rowId]: false }));
        }
    }, [rowRunsCache, rowRunsLoading]);
    const toggleExpand = (id: string, e: React.MouseEvent) => {
        e.stopPropagation();
        setExpandedRowId(prev => (prev === id ? null : id));
    };
    useEffect(() => {
        if (expandedRowId)
            fetchRowRuns(expandedRowId);
    }, [expandedRowId]);
    useEffect(() => {
        const token = localStorage.getItem('authToken') || '';
        if (!token)
            return;
        fetch(`${HIST_BASE}/history?page=1&limit=1`, { headers: { Authorization: `Bearer ${token}` } })
            .then(r => r.ok ? r.json() : null)
            .then(j => { if (j?.stats?.total)
            setHistoryCount(j.stats.total); })
            .catch(() => { });
    }, []);
    const { triggerRun, reattachRun, getBackendId, setPanelView, selectedRow, setSelectedRow, setSelectedQuoteRun, requestOpen, requestClose, renewals, renewalWindow, refreshRenewals, settings, prospects, prospectsTotal, prospectsPage, prospectsTotalPages, refreshProspects, refreshOpportunities, clearOpportunities, historyRuns, refreshSettings, } = useRateQuoteLink();
    // Fetch prospects + settings (incl. voice-settings) when this page mounts
    // Skip default prospect load when auto-quote will search by name instead
    const hasAutoQuote = Boolean(new URLSearchParams(window.location.search).get('autoQuote_insured'));
    useEffect(() => { if (!hasAutoQuote) refreshProspects(); refreshSettings(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
    const { setSelectedInsuredId } = useInsuredDetail();
    const openRQLPanel = useCallback(() => {
        setSelectedInsuredId(null);
        requestOpen();
    }, [setSelectedInsuredId, requestOpen]);
    const SEGMENT_FOR_TAB: Record<TabKey, string> = { classic: '', new: 'new', renewal: 'renewal', import: 'import', history: 'history', winback: 'winback', proposals: 'proposals', settings: 'settings' };
    const setActiveTab = useCallback((tab: TabKey) => {
        setActiveTabRaw(tab);
        setActiveInlineRunId(null);
        setInlineInitialMessage(undefined);
        setExpandedRowId(null);
        setSelectedRow(null);
        setPanelView('details');
        setSelectedRowIds(new Set());
        setSelectedInsuredId(null);
        const seg = SEGMENT_FOR_TAB[tab];
        navigate(`${ROUTES.RATE_QUOTING}${seg ? `/${seg}` : ''}`, { replace: true });
    }, [setSelectedRow, setPanelView, setSelectedInsuredId, navigate]);
    const openRunInline = useCallback((runId: string) => {
        setActiveInlineRunId(runId);
        const backendId = getBackendId(runId);
        if (backendId && backendId !== runId) {
            navigate(`${ROUTES.RATE_QUOTING}/run/${backendId}`, { replace: true });
        }
    }, [navigate, getBackendId]);
    useEffect(() => {
        if (!activeInlineRunId)
            return;
        const backendId = getBackendId(activeInlineRunId);
        if (backendId && backendId !== activeInlineRunId) {
            const currentPath = window.location.pathname;
            const expectedPath = `${ROUTES.RATE_QUOTING}/run/${backendId}`;
            if (!currentPath.includes(backendId)) {
                navigate(expectedPath, { replace: true });
            }
        }
    });
    const urlReattachedRef = useRef<string | null>(null);
    useEffect(() => {
        if (!urlRunId || urlReattachedRef.current === urlRunId)
            return;
        const timer = setTimeout(() => {
            const token = localStorage.getItem('authToken');
            if (!token)
                return;
            urlReattachedRef.current = urlRunId;
            reattachRun(urlRunId).then(() => {
                setActiveInlineRunId(urlRunId);
            });
        }, 100);
        return () => clearTimeout(timer);
    }, [urlRunId, reattachRun]);
    const autoSelectedRef = useRef(false);
    useEffect(() => {
        if (autoSelectedRef.current)
            return;
        if (!prospects || prospects.length === 0)
            return;
        autoSelectedRef.current = true;
        const first = prospects[0];
        setSelectedRow(first);
        setPanelView('details');
        refreshOpportunities(first.id);
    }, [prospects]);

    // ── Auto-Quote Deep Link ──
    // When the page loads with autoQuote params (from Rate SSO deep link via URL query),
    // find the insured and auto-trigger Execute.
    // Priority: 1) exact insuredId match  2) exact name match  3) fuzzy name match
    // Also triggers a search to handle insured on page 2+ of prospects.
    // Params are passed via URL query string (not localStorage) so they can't be
    // wiped by clearLocalStorageKeepingWidgets or leaked to other tabs.
    const autoQuoteTriggeredRef = useRef(false);
    const autoQuoteSearchedRef = useRef(false);
    const autoQuoteParams = useRef(() => {
        const sp = new URLSearchParams(window.location.search);
        const insured = sp.get('autoQuote_insured');
        const lob = sp.get('autoQuote_lob');
        const insuredId = sp.get('autoQuote_insuredId');
        if (insured && lob) {
            sp.delete('autoQuote_insured');
            sp.delete('autoQuote_lob');
            sp.delete('autoQuote_insuredId');
            const clean = sp.toString();
            window.history.replaceState({}, '', `${window.location.pathname}${clean ? `?${clean}` : ''}`);
            return { insured, lob, insuredId };
        }
        return null;
    }).current;
    const autoQuoteData = useRef(autoQuoteParams());
    useEffect(() => {
        if (autoQuoteTriggeredRef.current) return;
        if (!autoQuoteData.current) return;
        const { insured } = autoQuoteData.current;

        // Search both prospects and renewals by insured name
        if (!autoQuoteSearchedRef.current) {
            autoQuoteSearchedRef.current = true;
            refreshProspects({ search: insured });
            refreshRenewals({ search: insured });
            return;
        }

        // Wait for both search results
        if (!prospects || !renewals) return;

        const { insuredId, lob } = autoQuoteData.current;
        autoQuoteTriggeredRef.current = true;

        const normalize = (s: string) => s.toLowerCase().replace(/,/g, '').replace(/\s+/g, ' ').trim();
        const target = normalize(insured);

        const findMatch = (rows: QuoteRow[]): QuoteRow | undefined => {
            if (insuredId) {
                const idMatch = rows.find(row => row.insuredId === insuredId || row.id === insuredId);
                if (idMatch) return idMatch;
            }
            const exactMatch = rows.find(row => normalize(row.insured) === target);
            if (exactMatch) return exactMatch;
            return rows.find(row => {
                const n = normalize(row.insured);
                const parts = target.split(' ');
                if (parts.length === 2 && n === `${parts[1]} ${parts[0]}`) return true;
                return n.includes(target) || target.includes(n);
            });
        };

        // Search prospects first, then renewals
        const prospectMatch = findMatch(prospects);
        const renewalMatch = !prospectMatch ? findMatch(renewals) : undefined;
        const match = prospectMatch || renewalMatch;
        const isRenewal = Boolean(renewalMatch && !prospectMatch);

        if (!match) {
            console.warn(`[AutoQuote] Insured "${insured}"${insuredId ? ` (id=${insuredId})` : ''} not found in prospects or renewals`);
            refreshProspects();
            return;
        }

        const policyType = (lob as 'PAP' | 'HO' | 'BUNDLE') || 'PAP';
        const runId = triggerRun({
            rowId: match.id,
            workflow: isRenewal ? 'renewal' : 'new',
            label: `${isRenewal ? 'Renewal' : 'New'} — ${match.insured}`,
            insuredName: match.insured,
            insuredId: match.insuredId || insuredId || undefined,
            withReview: false,
            policyType,
        });
        if (runId) {
            openRunInline(runId);
            requestClose();
        } else {
            setPanelView('workflow');
            openRQLPanel();
        }
    }, [prospects, renewals]);

    const prospectsLoading = prospects === null;
    const newRows: QuoteRow[] = useMemo(() => prospects ?? [], [prospects]);
    const [voiceCallMap, setVoiceCallMap] = useState<Record<string, {
        call_status: string;
        interested: boolean | null;
        ai_outcome: string | null;
    }>>({});
    const [voiceCallsAll, setVoiceCallsAll] = useState<Record<string, any[]>>({});
    const [voiceHistoryTarget, setVoiceHistoryTarget] = useState<string | null>(null);
    const [voiceHistoryAnchor, setVoiceHistoryAnchor] = useState<{
        top: number;
        right: number;
    } | null>(null);
    const voiceEnabled = !!settings.voice?.enabled;
    const voicePollRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const voiceCallMapRef = useRef(voiceCallMap);
    voiceCallMapRef.current = voiceCallMap;
    const VOICE_TERMINAL = ['completed', 'failed', 'no_answer'];
    const VOICE_POLL_MS = 20000;
    const parseVoiceCalls = useCallback((json: any): Record<string, {
        call_status: string;
        interested: boolean | null;
        ai_outcome: string | null;
    }> => {
        const calls: any[] = Array.isArray(json) ? json : Array.isArray(json?.calls) ? json.calls : [];
        const map: Record<string, {
            call_status: string;
            interested: boolean | null;
            ai_outcome: string | null;
        }> = {};
        const allByProspect: Record<string, any[]> = {};
        for (const c of calls) {
            if (!c.prospect_id)
                continue;
            if (!map[c.prospect_id]) {
                map[c.prospect_id] = {
                    call_status: c.call_status,
                    interested: c.interested ?? null,
                    ai_outcome: c.ai_outcome ?? null,
                };
            }
            if (!allByProspect[c.prospect_id])
                allByProspect[c.prospect_id] = [];
            allByProspect[c.prospect_id].push(c);
        }
        setVoiceCallsAll(allByProspect);
        return map;
    }, []);
    const startVoicePoll = useCallback(() => {
        if (voicePollRef.current)
            return;
        const authToken = localStorage.getItem('authToken') || '';
        const ncToken = localStorage.getItem('amsToken') || '';
        voicePollRef.current = setInterval(async () => {
            const pending = Object.values(voiceCallMapRef.current).some(v => v.call_status === 'calling');
            if (!pending) {
                clearInterval(voicePollRef.current!);
                voicePollRef.current = null;
                return;
            }
            try {
                const resp = await fetch(`${RATE_QUOTING_BASE}/voice-calls/sync`, {
                    method: 'POST',
                    headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json' },
                    body: JSON.stringify({ ncToken }),
                });
                if (!resp.ok)
                    return;
                const fresh = parseVoiceCalls(await resp.json());
                setVoiceCallMap(prev => {
                    const next = { ...prev };
                    let changed = false;
                    for (const [pid, entry] of Object.entries(fresh)) {
                        if (prev[pid]?.call_status === 'calling' && VOICE_TERMINAL.includes(entry.call_status)) {
                            next[pid] = entry;
                            changed = true;
                        }
                    }
                    return changed ? next : prev;
                });
            }
            catch { }
        }, VOICE_POLL_MS);
    }, [parseVoiceCalls]);
    useEffect(() => {
        if (!voiceEnabled)
            return;
        const authToken = localStorage.getItem('authToken');
        if (!authToken)
            return;
        fetch(`${RATE_QUOTING_BASE}/voice-calls`, { headers: { Authorization: `Bearer ${authToken}` } })
            .then(r => r.ok ? r.json() : null)
            .then(json => {
            if (!json)
                return;
            const map = parseVoiceCalls(json);
            setVoiceCallMap(map);
        })
            .catch(() => { });
        return () => { if (voicePollRef.current) {
            clearInterval(voicePollRef.current);
            voicePollRef.current = null;
        } };
    }, [voiceEnabled]);
    const [callState, setCallState] = useState<Record<string, 'idle' | 'calling' | 'done' | 'error'>>({});
    const handleCallClick = useCallback(async (row: QuoteRow, e: React.MouseEvent) => {
        e.stopPropagation();
        if (callState[row.id] === 'calling')
            return;
        setCallState(prev => ({ ...prev, [row.id]: 'calling' }));
        const authToken = localStorage.getItem('authToken') || '';
        const ncToken = localStorage.getItem('amsToken') || '';
        try {
            let freshToolboxToken = '';
            try {
                const tbResp = await fetch(`${(process.env.REACT_APP_AccountAMP_Url || 'https://momentumampapi-stage.insurancegig.com')}/api/account/toolboxtoken?nctoken=${encodeURIComponent(ncToken)}`);
                if (tbResp.ok) {
                    const tbJson = await tbResp.json();
                    freshToolboxToken = tbJson?.token || '';
                }
            }
            catch { }
            const resp = await fetch(`${RATE_QUOTING_BASE}/voice-calls/trigger`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ prospectId: row.insuredId || row.id, insuredName: row.insured, phone: row.phone || '', ncToken, toolboxToken: freshToolboxToken, prospectDetails: row.rawProspect || {} }),
            });
            const json = await resp.json();
            if (json?.success) {
                setCallState(prev => ({ ...prev, [row.id]: 'done' }));
                setVoiceCallMap(prev => ({ ...prev, [row.id]: { call_status: 'calling', interested: null, ai_outcome: 'calling' } }));
                startVoicePoll();
            }
            else {
                setCallState(prev => ({ ...prev, [row.id]: 'error' }));
                window.setTimeout(() => setCallState(prev => ({ ...prev, [row.id]: 'idle' })), 3000);
            }
        }
        catch {
            setCallState(prev => ({ ...prev, [row.id]: 'error' }));
            window.setTimeout(() => setCallState(prev => ({ ...prev, [row.id]: 'idle' })), 3000);
        }
    }, [callState, startVoicePoll]);
    const renderVoiceStatus = useCallback((rowId: string): React.ReactElement => {
        const vc = voiceCallMap[rowId];
        if (!vc)
            return <span className="rql-vs rql-vs--not-called"><span className="rql-vs__dot"/>Not called</span>;
        const outcome = vc.ai_outcome || vc.call_status;
        switch (outcome) {
            case 'calling': return <span className="rql-vs rql-vs--calling"><span className="rql-vs__dot"/>Calling…</span>;
            case 'no_answer': return <span className="rql-vs rql-vs--no-answer"><span className="rql-vs__dot"/>No answer</span>;
            case 'failed': return <span className="rql-vs rql-vs--failed"><span className="rql-vs__dot"/>Failed</span>;
            case 'interested': return <span className="rql-vs rql-vs--interested"><span className="rql-vs__dot"/>Interested</span>;
            case 'not_interested': return <span className="rql-vs rql-vs--not-interested"><span className="rql-vs__dot"/>Not interested</span>;
            case 'not_resolved': return <span className="rql-vs rql-vs--not-resolved"><span className="rql-vs__dot"/>Not resolved</span>;
            default: return <span className="rql-vs rql-vs--not-called"><span className="rql-vs__dot"/>{outcome || 'Pending'}</span>;
        }
    }, [voiceCallMap]);
    const renewalsLoading = renewals === null;
    const [newSearch, setNewSearch] = useState('');
    const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const _today60 = (() => {
        const fmt = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        const to = new Date();
        const from = new Date();
        from.setDate(from.getDate() - 60);
        return { start: fmt(from), end: fmt(to) };
    })();
    const [newDateRange, setNewDateRange] = useState<{
        start: string;
        end: string;
    }>(_today60);
    const [renewalSearch, setRenewalSearch] = useState('');
    const renewalSearchDebounceRef = useRef<number | null>(null);
    const [renewalRange, setRenewalRange] = useState<DateRange>(EMPTY_RANGE);
    const renewalRows: QuoteRow[] = useMemo(() => renewals ?? [], [renewals]);
    const activeRows = useMemo(() => {
        if (activeTab === 'new')
            return newRows;
        if (activeTab === 'renewal' && (renewalRange.start || renewalRange.end))
            return renewalRows.filter(row => {
                const d = parseRowDate(row.expirationDateRaw || row.expirationDate);
                if (!d)
                    return false;
                return inRange(d, renewalRange);
            });
        return renewalRows;
    }, [activeTab, newRows, renewalRows, renewalRange]);
    const filterActive = !!(renewalRange.start || renewalRange.end);
    const hiddenCount = (activeTab === 'renewal' && filterActive) ? renewalRows.length - activeRows.length : 0;
    const [renewalPage, setRenewalPage] = useState(0);
    const renewalTotalPages = Math.max(1, Math.ceil(activeRows.length / ROWS_PER_PAGE));
    useEffect(() => {
        if (renewalPage >= renewalTotalPages)
            setRenewalPage(0);
    }, [renewalTotalPages]);
    useEffect(() => { setRenewalPage(0); }, [renewalRange.start, renewalRange.end]);
    const page = activeTab === 'new' ? prospectsPage - 1 : renewalPage;
    const totalPages = activeTab === 'new' ? prospectsTotalPages : renewalTotalPages;
    const setPage = (n: number) => {
        if (activeTab === 'new') {
            refreshProspects({ search: newSearch, page: n + 1, fromDate: newDateRange.start || undefined, toDate: newDateRange.end || undefined });
        }
        else {
            setRenewalPage(n);
        }
    };
    const pageStart = activeTab === 'new' ? 0 : renewalPage * ROWS_PER_PAGE;
    const pageEnd = activeTab === 'new' ? activeRows.length : Math.min(pageStart + ROWS_PER_PAGE, activeRows.length);
    const pagedRows = useMemo(() => activeTab === 'new' ? activeRows : activeRows.slice(pageStart, pageEnd), [activeTab, activeRows, pageStart, pageEnd]);
    useEffect(() => {
        if (searchDebounceRef.current)
            clearTimeout(searchDebounceRef.current);
        searchDebounceRef.current = setTimeout(() => {
            refreshProspects({
                search: newSearch,
                page: 1,
                fromDate: newDateRange.start || undefined,
                toDate: newDateRange.end || undefined,
            });
        }, 350);
        return () => { if (searchDebounceRef.current)
            clearTimeout(searchDebounceRef.current); };
    }, [newSearch, newDateRange.start, newDateRange.end]);
    useEffect(() => {
        if (renewalWindow && !renewalRange.start && !renewalRange.end) {
            setRenewalRange({ start: renewalWindow.start, end: renewalWindow.end });
        }
    }, [renewalWindow]);
    useEffect(() => {
        if (renewalSearchDebounceRef.current)
            clearTimeout(renewalSearchDebounceRef.current);
        renewalSearchDebounceRef.current = window.setTimeout(() => {
            refreshRenewals({
                daysBeforeExpiry: settings.renewal.daysBeforeExpiry,
                start: renewalRange.start || undefined,
                end: renewalRange.end || undefined,
                search: renewalSearch.trim() || undefined,
            });
        }, 350);
        return () => { if (renewalSearchDebounceRef.current)
            clearTimeout(renewalSearchDebounceRef.current); };
    }, [renewalRange.start, renewalRange.end, renewalSearch, settings.renewal.daysBeforeExpiry, refreshRenewals]);
    const selectedRowId = selectedRow?.id ?? null;
    const onRowClick = (row: QuoteRow) => {
        const deselecting = selectedRowId === row.id;
        setSelectedRow(deselecting ? null : row);
        setActiveInlineRunId(null);
        setInlineInitialMessage(undefined);
        setPanelView('details');
        setSelectedInsuredId(null);
        if (!deselecting && activeTab === 'new') {
            refreshOpportunities(row.id);
        }
        else if (deselecting || activeTab !== 'new') {
            clearOpportunities();
        }
    };
    const toggleRowSelect = (id: string, e: React.MouseEvent) => {
        e.stopPropagation();
        setSelectedRowIds(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };
    const allPageSelected = pagedRows.length > 0 && pagedRows.every(r => selectedRowIds.has(r.id));
    const toggleSelectAll = () => {
        setSelectedRowIds(prev => {
            if (allPageSelected)
                return new Set();
            return new Set(pagedRows.map(r => r.id));
        });
    };
    const bulkExecute = async (mode: ExecuteMode) => {
        if (selectedRowIds.size === 0 || bulkRunning)
            return;
        setBulkRunning(true);
        const ids = Array.from(selectedRowIds);
        for (const rowId of ids) {
            const row = pagedRows.find(r => r.id === rowId);
            if (!row)
                continue;
            const workflow = activeTab as 'new' | 'renewal';
            triggerRun({
                rowId: row.id,
                workflow,
                label: `${workflow === 'new' ? 'New' : 'Renewal'} — ${row.insured}`,
                insuredName: row.insured,
                insuredId: row.insuredId,
                withReview: mode === 'review',
                effectiveDate: workflow === 'renewal' ? row.expirationDateRaw : undefined,
            });
            await new Promise(res => setTimeout(res, 600));
        }
        setSelectedRowIds(new Set());
        setBulkRunning(false);
        setPanelView('workflow');
        openRQLPanel();
    };
    return (<div className="rql-page">
      <motion.div className="rql-body" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>
        <div className="rql-main">
          
          {plrConfigured === false ? (<div className="rql-setup-state">
              
              <div className="rql-setup-state__logos">
                <img src={logo} alt="Momentum AMP" className="rql-setup-state__logo-amp"/>
                <span className="rql-setup-state__logo-sep">
                  {plrSetupBusy
                ? <FiRefreshCw size={13} className="rql-setup-state__orb-spin"/>
                : '×'}
                </span>
                <img src={rateLogo} alt="Momentum Rate" className="rql-setup-state__logo-rate"/>
              </div>

              
              <div className="rql-setup-state__title">
                Quote every new lead in seconds —<br />AI-powered, carrier-ready.
              </div>
              <div className="rql-setup-state__desc">
                Personal Lines Rating connects your prospects list directly to the Rate API
                through AI-driven MCP servers. Select any lead and let our AI instantly
                generate accurate PAP and Home-Owners carrier quotes — no manual entry,
                no back-and-forth, no switching tabs.
              </div>

              <div className="rql-setup-state__features">
                <span className="rql-setup-state__tag"><RiSparklingFill size={11}/> AI + MCP Powered</span>
                <span className="rql-setup-state__tag"><FiCheck size={12}/> PAP &amp; Home-Owners</span>
                <span className="rql-setup-state__tag"><FiZap size={11}/> Instant Lead Quotes</span>
                <span className="rql-setup-state__tag"><FiLock size={11}/> Scoped to your account</span>
              </div>

              <div className="rql-setup-state__actions">
                <button className="rql-setup-state__btn" onClick={handleSetupPlr} disabled={plrSetupBusy}>
                  {plrSetupBusy
                ? <><FiRefreshCw size={12} className="rql-setup-state__orb-spin"/> Connecting Rate API…</>
                : <><FiZap size={12}/> Click here to launch setup</>}
                </button>
              </div>

              {plrSetupError && plrSetupError === 'NOT_SUBSCRIBED' ? (<div className="rql-setup-state__subscribe">
                  <FiLock size={14}/>
                  <div>
                    <strong>Momentum Rate subscription required</strong>
                    <p>To use Personal Lines Rating, you need an active Momentum Rate subscription. Please subscribe to get started.</p>
                  </div>
                </div>) : plrSetupError ? (<div className="rql-setup-state__error">
                  <FiAlertTriangle size={12}/> {plrSetupError}
                </div>) : null}

              <div className="rql-setup-state__note">
                Takes around 15–30 seconds. Your prospects list will load automatically when setup completes.
              </div>
            </div>) : plrConfigured === null ? (<div className="rql-skel">
              <div className="rql-skel__card">
                <div className="rql-skel__logos-row">
                  <div className="rql-skel__logo-circle"/>
                  <div className="rql-skel__logo-sep"/>
                  <div className="rql-skel__logo-circle"/>
                </div>
                <div className="rql-skel__line rql-skel__line--title"/>
                <div className="rql-skel__line rql-skel__line--title rql-skel__line--title-short"/>
                <div className="rql-skel__line rql-skel__line--desc"/>
                <div className="rql-skel__line rql-skel__line--desc"/>
                <div className="rql-skel__line rql-skel__line--desc rql-skel__line--desc-short"/>
                <div className="rql-skel__tags">
                  <div className="rql-skel__tag"/>
                  <div className="rql-skel__tag"/>
                  <div className="rql-skel__tag"/>
                  <div className="rql-skel__tag"/>
                </div>
                <div className="rql-skel__btn"/>
              </div>
            </div>) : (<>
          
          {activeInlineRunId ? (<InlineWorkflowPanel initialMessage={inlineInitialMessage} onBack={() => {
                    setActiveInlineRunId(null);
                    setInlineInitialMessage(undefined);
                    setPanelView('details');
                    openRQLPanel();
                    const seg = SEGMENT_FOR_TAB[activeTab];
                    navigate(`${ROUTES.RATE_QUOTING}${seg ? `/${seg}` : ''}`, { replace: true });
                    if (expandedRowId) {
                        setRowRunsCache(prev => { const next = { ...prev }; delete next[expandedRowId]; return next; });
                        fetchRowRuns(expandedRowId, true);
                    }
                }} onRequote={({ rowId, workflow, label, insuredName, insuredId }) => {
                    triggerRun({ rowId, workflow, label, insuredName, insuredId, withReview: true });
                    setActiveInlineRunId(null);
                    setPanelView('workflow');
                    openRQLPanel();
                }}/>) : (<>
          
          <div className="rql-toolbar">
            <div className="rql-tabs" role="tablist" aria-label="Quote type">
              <button role="tab" aria-selected={activeTab === 'classic'} className={`rql-tab${activeTab === 'classic' ? ' rql-tab--active' : ''}`} onClick={() => setActiveTab('classic')}>
                <RiSparklingFill size={14}/>
                Classic Quote
              </button>
              <button role="tab" aria-selected={activeTab === 'new'} className={`rql-tab${activeTab === 'new' ? ' rql-tab--active' : ''}`} onClick={() => setActiveTab('new')}>
                <FiPlus size={14}/>
                Prospects
                <span className="rql-tab__count">{prospectsTotal || newRows.length}</span>
              </button>
              <button role="tab" aria-selected={activeTab === 'renewal'} className={`rql-tab${activeTab === 'renewal' ? ' rql-tab--active' : ''}`} onClick={() => setActiveTab('renewal')}>
                <FiRefreshCw size={14}/>
                Renewal
                <span className="rql-tab__count">{renewalRows.length}</span>
              </button>
              <button role="tab" aria-selected={activeTab === 'import'} className={`rql-tab${activeTab === 'import' ? ' rql-tab--active' : ''}`} onClick={() => setActiveTab('import')}>
                <FiFileText size={14}/>
                Import
              </button>
              <button role="tab" aria-selected={activeTab === 'history'} className={`rql-tab${activeTab === 'history' ? ' rql-tab--active' : ''}`} onClick={() => setActiveTab('history')}>
                <FiClock size={14}/>
                AI Quote History
                {historyCount > 0 && <span className="rql-tab__count">{historyCount}</span>}
              </button>
              <button role="tab" aria-selected={activeTab === 'proposals'} className={`rql-tab${activeTab === 'proposals' ? ' rql-tab--active' : ''}`} onClick={() => setActiveTab('proposals')}>
                <FiFileText size={14}/>
                Quote Proposals
              </button>
              <button role="tab" aria-selected={activeTab === 'settings'} className={`rql-tab${activeTab === 'settings' ? ' rql-tab--active' : ''}`} onClick={() => setActiveTab('settings')}>
                <FiSettings size={14}/>
                Settings
              </button>
            </div>

            

            {false && selectedRowIds.size > 0 && (<div className="rql-bulk-bar">
                <span className="rql-bulk-bar__count">
                  <FiLayers size={12}/> {selectedRowIds.size} selected
                </span>
                <button type="button" className="rql-bulk-bar__btn" disabled={bulkRunning} onClick={() => bulkExecute('direct')} title="Execute all selected rows">
                  {bulkRunning ? <FiRefreshCw size={11} className="rql-row-execute__spin"/> : <FiZap size={11}/>}
                  Execute All
                </button>
                <button type="button" className="rql-bulk-bar__btn rql-bulk-bar__btn--review" disabled={bulkRunning} onClick={() => bulkExecute('review')} title="Execute all selected with review">
                  <FiCheck size={11}/> With Review
                </button>
                <button type="button" className="rql-bulk-bar__clear" onClick={() => setSelectedRowIds(new Set())} title="Clear selection">
                  <FiX size={11}/>
                </button>
              </div>)}

          </div>

          {activeTab === 'classic' ? (<ClassicQuoteTab onSwitchToImport={() => setActiveTab('import')} onStartQuote={({ clientId, clientName, policyType, state, mode }) => {
                    const runId = triggerRun({
                      rowId: clientId,
                      workflow: 'new',
                      label: `New — ${clientName}`,
                      insuredName: clientName,
                      insuredId: clientId,
                      withReview: mode === 'review',
                      policyType,
                    });
                    if (runId) {
                      setActiveTabRaw('new');
                      openRunInline(runId);
                    }
                  }} />) : activeTab === 'proposals' ? (<QuoteProposalsTab />) : activeTab === 'settings' ? (<PLRSkillSettings />) : activeTab === 'import' ? (<RateQuoteLinkImportTab onQuoteStarted={(runId: string) => {
                        reattachRun(runId).then(() => {
                            setActiveTabRaw('new');
                            openRunInline(runId);
                        });
                    }} onInsertedProspect={() => {
                        refreshProspects({
                            search: newSearch,
                            fromDate: newDateRange.start || undefined,
                            toDate: newDateRange.end || undefined,
                        });
                    }}/>) : activeTab === 'winback' ? (<div className="rql-coming-soon">
              <div className="rql-coming-soon__halo">
                <div className="rql-coming-soon__icon">
                  <RiSparklingFill size={26}/>
                </div>
              </div>
              <div className="rql-coming-soon__badge">Coming soon</div>
              <div className="rql-coming-soon__title">Winback workflow</div>
              <div className="rql-coming-soon__hint">
                AI-driven re-engagement for lapsed insureds & lost prospects.
                Auto-detect non-renewals, refresh quotes against current carrier
                appetite, and surface the best path back to bound coverage.
              </div>
              <div className="rql-coming-soon__chips">
                <span className="rql-coming-soon__chip">Lapsed-policy detection</span>
                <span className="rql-coming-soon__chip">AI re-quote</span>
                <span className="rql-coming-soon__chip">Producer hand-off</span>
              </div>
            </div>) : activeTab === 'history' ? (<HistoryTab runs={historyRuns} onOpenRun={(backendRunId) => {
                        const localId = `run-reattach-${backendRunId}`;
                        reattachRun(backendRunId).then(() => {
                            setActiveInlineRunId(localId);
                            navigate(`${ROUTES.RATE_QUOTING}/run/${backendRunId}`, { replace: true });
                        });
                    }}/>) : (<>
          
          {(activeTab === 'new' || activeTab === 'renewal') && (<div className="rql-filter-bar">
              <div className="rql-hist__search">
                <FiSearch size={13}/>
                <input type="text" placeholder={activeTab === 'new' ? 'Search prospects...' : 'Search renewals...'} value={activeTab === 'new' ? newSearch : renewalSearch} onChange={e => activeTab === 'new' ? setNewSearch(e.target.value) : setRenewalSearch(e.target.value)}/>
                {(activeTab === 'new' ? newSearch : renewalSearch) && (<button className="rql-hist__date-clear" onClick={() => activeTab === 'new' ? setNewSearch('') : setRenewalSearch('')}>
                    <FiX size={11}/>
                  </button>)}
              </div>
              <div className="rql-hist__date-range">
                <FiClock size={11}/>
                <input type="date" value={activeTab === 'new' ? newDateRange.start : renewalRange.start} onChange={e => activeTab === 'new'
                            ? setNewDateRange(r => ({ ...r, start: e.target.value }))
                            : setRenewalRange(r => ({ ...r, start: e.target.value }))}/>
                <span className="rql-hist__date-sep">–</span>
                <input type="date" value={activeTab === 'new' ? newDateRange.end : renewalRange.end} onChange={e => activeTab === 'new'
                            ? setNewDateRange(r => ({ ...r, end: e.target.value }))
                            : setRenewalRange(r => ({ ...r, end: e.target.value }))}/>
                {((activeTab === 'new' && (newDateRange.start !== _today60.start || newDateRange.end !== _today60.end)) ||
                            (activeTab === 'renewal' && filterActive)) && (<button className="rql-hist__date-clear" onClick={() => activeTab === 'new' ? setNewDateRange(_today60) : setRenewalRange(EMPTY_RANGE)}>
                    <FiX size={11}/>
                  </button>)}
              </div>
              <span className="rql-hist__count">
                {activeTab === 'new' ? prospectsTotal : activeRows.length}{' '}
                {activeTab === 'new' ? 'prospect' : 'renewal'}{(activeTab === 'new' ? prospectsTotal : activeRows.length) !== 1 ? 's' : ''}
              </span>
            </div>)}

          
          <div className="rql-table-card">
            <div className="rql-table-card__header">
              <span className="rql-table-card__title">
                {activeTab === 'new' ? 'New Business Quotes' : 'Renewal Quotes'}
              </span>
              <span className="rql-table-card__subtitle">
                {activeTab === 'new' ? prospectsTotal : activeRows.length}{' '}
                {(activeTab === 'new' ? prospectsTotal : activeRows.length) === 1 ? 'record' : 'records'}
                {hiddenCount > 0 && (<span className="rql-table-card__filtered">
                    {' '}· {hiddenCount} hidden by filter
                  </span>)}
              </span>
            </div>


            <div className="rql-table-wrap">
              <table className="rql-table">
                <thead>
                  <tr>
                    <th className="rql-table__col-check" style={{ display: 'none' }}>
                      <button type="button" className="rql-check" onClick={toggleSelectAll} title="Select all on page">
                        {allPageSelected ? <FiCheckSquare size={13}/> : <FiSquare size={13}/>}
                      </button>
                    </th>
                    <th className="rql-table__col-expand"/>
                    <th>Name</th>
                    {activeTab === 'renewal' && <th>Policy #</th>}
                    {activeTab === 'new' && <th>Location</th>}
                    {activeTab === 'new' && voiceEnabled && <th style={{ whiteSpace: 'nowrap' }}><FiPhone size={11} style={{ verticalAlign: 'middle', marginRight: 4 }}/>Voice Status</th>}
                    {activeTab === 'renewal' && <th>Line of Business</th>}
                    {activeTab === 'renewal' && <th>Eff. Date</th>}
                    {activeTab === 'renewal' && <th>Exp. Date</th>}
                    {activeTab === 'renewal' && <th>Premium</th>}
                    <th className="rql-table__col-action">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {(activeTab === 'new' && prospectsLoading) || (activeTab === 'renewal' && renewalsLoading) ? (Array.from({ length: SKEL_ROWS }, (_, i) => (<SkeletonRow key={i} tab={activeTab as TabKey}/>))) : activeRows.length === 0 ? (<tr>
                      <td colSpan={activeTab === 'renewal' ? 10 : (voiceEnabled ? 6 : 5)}>
                        <div className="rql-empty">
                          {activeTab === 'new'
                            ? (newSearch ? `No prospects match "${newSearch}".` : 'No prospects found for the selected date range.')
                            : (renewalSearch || filterActive)
                                ? `No renewals match${renewalSearch ? ` "${renewalSearch}"` : ''}${filterActive ? ' in the selected date range' : ''}.`
                                : 'No policies expiring in the configured window. Adjust "Execute before expiry" in Settings, or pick a wider date range above.'}
                        </div>
                      </td>
                    </tr>) : (pagedRows.map((row) => {
                        const isExpanded = expandedRowId === row.id;
                        const colSpan = activeTab === 'renewal' ? 10 : (voiceEnabled ? 6 : 5);
                        return (<React.Fragment key={row.id}>
                        <tr className={`rql-table__row${selectedRowId === row.id ? ' rql-table__row--selected' : ''}${isExpanded ? ' rql-table__row--expanded' : ''}`} onClick={() => onRowClick(row)}>
                          
                          <td className="rql-table__col-check" style={{ display: 'none' }} onClick={e => toggleRowSelect(row.id, e)}>
                            <span className={`rql-check${selectedRowIds.has(row.id) ? ' rql-check--on' : ''}`}>
                              {selectedRowIds.has(row.id) ? <FiCheckSquare size={13}/> : <FiSquare size={13}/>}
                            </span>
                          </td>
                          
                          <td className="rql-table__col-expand" onClick={e => toggleExpand(row.id, e)}>
                            <button type="button" className={`rql-expand-btn${isExpanded ? ' rql-expand-btn--open' : ''}`} aria-label="Toggle quote runs">
                              <FiChevronRight size={13}/>
                            </button>
                          </td>
                          <td className="rql-table__cell--primary">{row.insured}</td>
                          {activeTab === 'renewal' && <td className="rql-table__cell--muted">{row.policyNumber ?? '—'}</td>}
                          {activeTab === 'new' && <td>{row.notes || '—'}</td>}
                          {activeTab === 'new' && voiceEnabled && <td>{renderVoiceStatus(row.id)}</td>}
                          {activeTab === 'renewal' && <td>{row.lineOfBusiness || row.carrier || '—'}</td>}
                          {activeTab === 'renewal' && <td>{row.effectiveDate ?? '—'}</td>}
                          {activeTab === 'renewal' && <td>{row.expirationDate ?? '—'}</td>}
                          {activeTab === 'renewal' && <td>{row.premium}</td>}
                          <td className="rql-table__col-action" onClick={e => e.stopPropagation()} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            {activeTab === 'new' && voiceEnabled && (() => {
                                const cs = callState[row.id] ?? 'idle';
                                const vc = voiceCallMap[row.id];
                                const isNotInterested = vc?.ai_outcome === 'not_interested' || (vc?.ai_outcome == null && vc?.interested === false);
                                const alreadyCalled = !!vc && isNotInterested;
                                const hasCalls = !!vc || !!(voiceCallsAll[row.id]?.length);
                                const SZ = 28;
                                const isHistoryOpen = voiceHistoryTarget === row.id;
                                return (<div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>

                                  
                                  {hasCalls ? (<button type="button" title="Call history" onClick={e => {
                                            e.stopPropagation();
                                            const pid = row.id;
                                            if (isHistoryOpen) {
                                                setVoiceHistoryTarget(null);
                                                setVoiceHistoryAnchor(null);
                                            }
                                            else {
                                                const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                                                setVoiceHistoryAnchor({ top: rect.bottom + 6, right: window.innerWidth - rect.right });
                                                setVoiceHistoryTarget(pid);
                                                if (!voiceCallsAll[pid]?.length) {
                                                    const at = localStorage.getItem('authToken') || '';
                                                    fetch(`${RATE_QUOTING_BASE}/voice-calls`, { headers: { Authorization: `Bearer ${at}` } })
                                                        .then(r => r.ok ? r.json() : null)
                                                        .then(json => { if (json)
                                                        parseVoiceCalls(json); })
                                                        .catch(() => { });
                                                }
                                            }
                                        }} style={{
                                            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                                            width: SZ, height: SZ, borderRadius: 6,
                                            border: `1px solid ${isHistoryOpen ? 'rgba(162,0,255,0.5)' : 'rgba(162,0,255,0.2)'}`,
                                            background: isHistoryOpen ? 'rgba(162,0,255,0.14)' : 'rgba(162,0,255,0.05)',
                                            color: '#A200FF', cursor: 'pointer', transition: 'background 0.15s',
                                        }}>
                                      <FiClock size={12}/>
                                    </button>) : (<button type="button" disabled style={{
                                            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                                            width: SZ, height: SZ, borderRadius: 6,
                                            border: '1px solid rgba(0,0,0,0.07)',
                                            background: 'rgba(0,0,0,0.03)', color: 'rgba(0,0,0,0.18)', cursor: 'default',
                                        }}>
                                      <FiClock size={12}/>
                                    </button>)}

                                  
                                  {alreadyCalled && cs === 'idle' ? (<button type="button" disabled title="Not interested — no retry" style={{
                                            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                                            width: SZ, height: SZ, borderRadius: 6, border: 'none',
                                            background: 'rgba(0,0,0,0.06)', color: 'rgba(0,0,0,0.2)', cursor: 'default',
                                        }}>
                                      <FiPhone size={13}/>
                                    </button>) : (<button type="button" title={cs === 'calling' ? 'Calling…' : cs === 'done' ? 'Called' : cs === 'error' ? 'Retry' : vc ? 'Retry call' : 'Call prospect'} onClick={e => handleCallClick(row, e)} disabled={cs === 'calling' || cs === 'done'} style={{
                                            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                                            width: SZ, height: SZ, borderRadius: 6, border: 'none',
                                            cursor: cs === 'calling' || cs === 'done' ? 'not-allowed' : 'pointer',
                                            background: cs === 'done' ? '#22c55e' : cs === 'error' ? '#ef4444' : '#FF6820',
                                            color: '#fff', opacity: cs === 'calling' ? 0.6 : 1,
                                            transition: 'opacity 0.15s',
                                        }}>
                                      <FiPhone size={13}/>
                                    </button>)}

                                </div>);
                            })()}
                            <ExecuteSplitButton onRun={(mode, policyType) => {
                                const workflow = activeTab as 'new' | 'renewal';
                                const runId = triggerRun({
                                    rowId: row.id,
                                    workflow,
                                    label: `${workflow === 'new' ? 'New' : 'Renewal'} — ${row.insured}`,
                                    insuredName: row.insured,
                                    insuredId: row.insuredId,
                                    withReview: mode === 'review',
                                    policyType,
                                    effectiveDate: workflow === 'renewal' ? row.expirationDateRaw : undefined,
                                });
                                if (runId) {
                                    openRunInline(runId);
                                    requestClose();
                                }
                                else {
                                    setPanelView('workflow');
                                    openRQLPanel();
                                }
                            }}/>
                          </td>
                        </tr>
                        {isExpanded && (<RowExpand colSpan={colSpan} runs={rowRunsCache[row.id] ?? []} isLoading={rowRunsLoading[row.id] ?? false} onOpenWorkflow={(runId) => { setInlineInitialMessage(undefined); openRunInline(runId); requestClose(); }} onOpenWorkflowForEmail={(runId, carrierName) => { setInlineInitialMessage(`Please create an email draft for the ${carrierName} quote to send to the client.`); openRunInline(runId); requestClose(); }} onRefresh={() => fetchRowRuns(row.id, true)} onExecute={() => {
                                    const workflow = activeTab as 'new' | 'renewal';
                                    const runId = triggerRun({
                                        rowId: row.id,
                                        workflow,
                                        label: `${workflow === 'new' ? 'New' : 'Renewal'} — ${row.insured}`,
                                        insuredName: row.insured,
                                        insuredId: row.insuredId,
                                        withReview: false,
                                        effectiveDate: workflow === 'renewal' ? row.expirationDateRaw : undefined,
                                    });
                                    if (runId) {
                                        openRunInline(runId);
                                        requestClose();
                                    }
                                    else {
                                        setPanelView('workflow');
                                        openRQLPanel();
                                    }
                                }} onSelectRun={(run, runNumber) => {
                                    const isRenewal = activeTab === 'renewal';
                                    const rawPremium = isRenewal
                                        ? parseFloat((row.premium || '').replace(/[^0-9.]/g, '')) || null
                                        : null;
                                    setSelectedQuoteRun({
                                        runNumber,
                                        executedAt: run.executedAt,
                                        runStatus: run.runStatus,
                                        durationMs: run.durationMs,
                                        carriers: run.carriers.map(c => ({
                                            name: c.name,
                                            premium: c.premium,
                                            premiumRaw: c.premiumRaw,
                                            termLabel: c.termLabel,
                                            monthlyPremium: c.monthlyPremium,
                                            status: c.status,
                                            failureReason: c.failureReason,
                                            logoURL: c.logoURL,
                                            bridgingLink: c.bridgingLink,
                                            amBestRating: c.amBestRating,
                                        })),
                                        rawCoverages: run.rawCoverages,
                                        submittedDriversCount: run.submittedDriversCount,
                                        submittedVehiclesCount: run.submittedVehiclesCount,
                                        errorMessage: run.errorMessage,
                                        isRenewal,
                                        currentPremium: rawPremium,
                                        currentCarrier: isRenewal && row.carrier !== '—' ? row.carrier : null,
                                    });
                                    setPanelView('quote-detail');
                                    openRQLPanel();
                                }}/>)}
                        </React.Fragment>);
                    }))}
                </tbody>
              </table>
            </div>

            
            {totalPages > 1 && (<div className="rql-pagination">
                <span className="rql-pagination__summary">
                  {activeTab === 'new'
                            ? <>Showing <strong>{pagedRows.length}</strong> of <strong>{prospectsTotal}</strong></>
                            : <>Showing <strong>{pageStart + 1}–{pageEnd}</strong> of <strong>{activeRows.length}</strong></>}
                </span>
                <div className="rql-pagination__controls">
                  <button type="button" className="rql-pagination__btn" onClick={() => setPage(Math.max(0, page - 1))} disabled={page === 0} aria-label="Previous page">
                    <FiChevronLeft size={14}/>
                    <span>Prev</span>
                  </button>
                  <span className="rql-pagination__page">
                    Page <strong>{page + 1}</strong> of <strong>{totalPages}</strong>
                  </span>
                  <button type="button" className="rql-pagination__btn" onClick={() => setPage(Math.min(totalPages - 1, page + 1))} disabled={page >= totalPages - 1} aria-label="Next page">
                    <span>Next</span>
                    <FiChevronRight size={14}/>
                  </button>
                </div>
              </div>)}
          </div>
          </>)}

          
          {/* RateQuotingPageChatBar removed — GlobalChatBar (shell) is the single chat entry point */}
        </>)}
        </>)}
        </div>

      </motion.div>

      
      {voiceHistoryTarget && voiceHistoryAnchor && (() => {
            const calls = voiceCallsAll[voiceHistoryTarget] ?? [];
            const formatDate = (d: string | null) => {
                if (!d)
                    return '—';
                try {
                    return new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
                }
                catch {
                    return d;
                }
            };
            return (<>
            
            <div style={{ position: 'fixed', inset: 0, zIndex: 9998 }} onClick={() => { setVoiceHistoryTarget(null); setVoiceHistoryAnchor(null); }}/>
            <div style={{
                    position: 'fixed',
                    top: voiceHistoryAnchor.top,
                    right: voiceHistoryAnchor.right,
                    zIndex: 9999,
                    width: 360,
                    maxHeight: 440,
                    overflowY: 'auto',
                    background: '#fff',
                    border: '1px solid rgba(56,21,116,0.12)',
                    borderRadius: 12,
                    boxShadow: '0 8px 32px rgba(56,21,116,0.14), 0 2px 8px rgba(0,0,0,0.06)',
                    fontFamily: 'Poppins, sans-serif',
                }}>
              
              <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '12px 16px 10px',
                    borderBottom: '1px solid rgba(56,21,116,0.08)',
                    position: 'sticky', top: 0, background: '#fff', zIndex: 1,
                }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                  <FiPhone size={13} style={{ color: '#A200FF' }}/>
                  <span style={{ fontSize: 13, fontWeight: 600, color: '#381574' }}>Voice Call History</span>
                </div>
                <span style={{ fontSize: 11, color: '#9CA3AF' }}>{calls.length} call{calls.length !== 1 ? 's' : ''}</span>
              </div>
              
              <div style={{ padding: '8px 0' }}>
                {calls.length === 0 ? (<div style={{ padding: '24px 16px', textAlign: 'center', color: '#9CA3AF', fontSize: 12 }}>No calls recorded yet</div>) : calls.map((c: any) => {
                    const s = c.call_status;
                    const i = c.interested;
                    let badge = { label: s || 'Unknown', bg: 'rgba(156,163,175,0.12)', color: '#6B7280', dot: '#9CA3AF' };
                    if (s === 'calling')
                        badge = { label: 'Calling…', bg: 'rgba(96,165,250,0.10)', color: '#60A5FA', dot: '#60A5FA' };
                    else if (s === 'no_answer')
                        badge = { label: 'No answer', bg: 'rgba(245,158,11,0.10)', color: '#F59E0B', dot: '#F59E0B' };
                    else if (s === 'failed')
                        badge = { label: 'Failed', bg: 'rgba(239,68,68,0.10)', color: '#EF4444', dot: '#EF4444' };
                    else if (i === true)
                        badge = { label: 'Interested', bg: 'rgba(78,213,110,0.10)', color: '#4ED56E', dot: '#4ED56E' };
                    else if (i === false)
                        badge = { label: 'Not interested', bg: 'rgba(244,63,94,0.08)', color: '#F43F5E', dot: '#F43F5E' };
                    else if (s === 'completed')
                        badge = { label: 'Not resolved', bg: 'rgba(167,139,250,0.08)', color: '#A78BFA', dot: '#A78BFA' };
                    return (<div key={c.id} style={{ padding: '10px 16px', borderBottom: '1px solid rgba(0,0,0,0.04)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                        <span style={{ fontSize: 11.5, fontWeight: 600, color: '#1F0A40' }}>{c.insured_name || '—'}</span>
                        <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: 4,
                            padding: '2px 8px', borderRadius: 20,
                            fontSize: 10, fontWeight: 500,
                            background: badge.bg, color: badge.color,
                        }}>
                          <span style={{ width: 5, height: 5, borderRadius: '50%', background: badge.dot, flexShrink: 0 }}/>
                          {badge.label}
                        </span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 10.5, color: '#6B7280' }}>
                        <span><FiClock size={9} style={{ marginRight: 3, verticalAlign: 'middle' }}/>{formatDate(c.called_at || c.created_at)}</span>
                        {c.phone_number && <span><FiPhone size={9} style={{ marginRight: 3, verticalAlign: 'middle' }}/>{c.phone_number}</span>}
                      </div>
                      {c.transcript_summary && (<div style={{
                                marginTop: 7, padding: '7px 10px', borderRadius: 7,
                                background: 'rgba(56,21,116,0.04)', fontSize: 11, color: '#4B5563', lineHeight: 1.5,
                            }}>
                          {c.transcript_summary}
                        </div>)}
                    </div>);
                })}
              </div>
            </div>
          </>);
        })()}
    </div>);
};
export default RateQuoteLinkPage;
